import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, differenceInDays, addDays, getDate, getDay, startOfDay, endOfDay } from 'date-fns';
import {
  Calendar,
  CalendarDays,
  Users,
  Briefcase,
  MapPin,
  History,
  FileText,
  ArrowLeft,
  ArrowDown,
  ArrowRight,
  Repeat,
  Moon,
  Save,
  Trash2,
  X,
  Globe,
  SlidersHorizontal,
  Sparkles,
  ChevronsUpDown,
  AlertTriangle,
  Check,
  ChevronDown,
} from 'lucide-react';

import { SheetFooter } from '@/components/ui/sheet';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { Alert, AlertTitle, AlertDescription, AlertClose } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

import {
  Booking,
  BookingStatus,
  BookingAuditEntry,
  DurationType,
  HalfDayPeriod,
  RecurrencePattern,
  RecurrenceEndType,
  Resource,
  ProjectDetails,
  Client,
  CostCentre,
  FieldConfiguration,
  TabConfiguration,
} from '@/types';

import { useScheduler, fetchAuditHistory, DEFAULT_STATUS_CONFIGS, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { useTour } from '@/contexts/TourContext';
import { useCompany } from '@/contexts/CompanyContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { MultiResourceSelect } from '@/components/booking/MultiResourceSelect';
import { EnhancedMultiResourceSelect } from '@/components/booking/EnhancedMultiResourceSelect';
import { RecurrenceDialog } from '@/components/booking/RecurrenceDialog';
import { BookingSaveCancelledError } from '@/lib/booking-save-cancelled';
import { EnhancedProjectSelect } from '@/components/booking/EnhancedProjectSelect';
import { EnhancedCostCentreSelect } from '@/components/booking/EnhancedCostCentreSelect';
import { EnhancedCustomerSelect } from '@/components/booking/EnhancedCustomerSelect';
import { TIMEZONES } from '@/lib/timezones';
import { recurrenceSettingsApi, getCurrentResourceBookingTypeUsages, BookingTypeUsageDto } from '@/services/api';
import { Checkbox } from '@/components/ui/checkbox';
import { ResourceRecommendationDialog } from '@/components/booking/ResourceRecommendationDialog';
import { evaluateFilter } from '@/utils/filterEvaluation';
import { calculateResourceUtilization } from '@/lib/utils/calendar';
import { getBookingSegments } from '@/utils/booking-conflict';
import {
  UNALLOCATED_RESOURCE_ID,
  filterResourcesByWorkingDays,
  getBookingResourceFields,
  getBookingStatusForResourceSelection,
  getResourcePickerOptions,
  hasResourceSelection,
  normalizeResourceSelection,
} from '@/utils/unallocated-booking';
import { formatHourAsTime } from '@/lib/hourOfDay';

interface ConflictDetail {
  resourceId: string;
  resourceName: string;
  conflictingBookingId: string | number;
  conflictingProjectName: string;
  overlapStart: Date;
  overlapEnd: Date;
  conflictingStartTime: string;
  conflictingEndTime: string;
}

interface ComprehensiveBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Callbacks from ResourceCalendar
  onSave?: (booking: Omit<Booking, 'id'>) => Promise<void>;
  onUpdate?: (id: string | number, booking: Partial<Booking>) => Promise<void>;
  onDelete?: (booking: Booking) => void;
  // Data props from ResourceCalendar
  resources?: Resource[];
  projectDetails?: ProjectDetails[];
  costCentres?: CostCentre[];
  existingBookings?: Booking[];
  // Preselection props
  preselectedResource?: Resource;
  preselectedDate?: Date;
  preselectedEndDate?: Date;
  preselectedStartTime?: string;
  preselectedEndTime?: string;
  preselectedBookingData?: Partial<Booking>;
  // Editing
  editingBooking?: Booking | null;
  editContextResourceId?: string;
  mode?: 'create' | 'edit';
  dataMode?: 'demo' | 'live';
  // Timezone currently selected on the timeline; used as the default for new bookings
  defaultTimezone?: string;
  // Legacy props (for standalone usage)
  initialStartDate?: Date;
  initialEndDate?: Date;
  initialResourceId?: string;
}

export function ComprehensiveBookingModal({
  open,
  onOpenChange,
  onSave,
  onUpdate,
  onDelete,
  resources: propResources,
  projectDetails: propProjectDetails,
  costCentres: propCostCentres,
  existingBookings: propExistingBookings,
  preselectedResource,
  preselectedDate,
  preselectedEndDate,
  preselectedStartTime,
  preselectedEndTime,
  preselectedBookingData,
  editingBooking,
  editContextResourceId,
  mode = 'create',
  dataMode,
  defaultTimezone,
  initialStartDate,
  initialEndDate,
  initialResourceId,
}: ComprehensiveBookingModalProps) {
  const {
    resources: contextResources,
    bookingTypes,
    projectDetails,
    costCentres: contextCostCentres,
    clients: contextClients,
    liveData,
    addBooking,
    updateBooking,
    deleteBooking,
    settings,
    mockData,
    demoBookingTypeUsages,
  } = useScheduler();

  const { canEditBooking, canDeleteBooking, canOverrideReadOnly } = usePermissions();

  // ── Booking Type Usage permissions ─────────────────────────────────────────
  // null = still loading; [] = loaded (user resolved but no usages, or user not resolved)
  const [bookingTypeUsages, setBookingTypeUsages] = useState<BookingTypeUsageDto[] | null>(null);
  const [userResolved, setUserResolved] = useState(false);

  useEffect(() => {
    // In demo mode use the context-provided demoBookingTypeUsages instead of API
    if (dataMode === 'demo') {
      setUserResolved(true);
      setBookingTypeUsages(demoBookingTypeUsages);
      return;
    }

    let cancelled = false;
    getCurrentResourceBookingTypeUsages()
      .then(usages => {
        if (!cancelled) {
          setUserResolved(true);
          setBookingTypeUsages(usages);
        }
      })
      .catch(() => { if (!cancelled) setBookingTypeUsages([]); });
    return () => { cancelled = true; };
  }, [dataMode, demoBookingTypeUsages]);

  // In demo mode use company-filtered booking types; in live mode use all
  const effectiveBookingTypes = useMemo(() => {
    if (dataMode === 'demo') return mockData.bookingTypes;
    return bookingTypes;
  }, [dataMode, mockData.bookingTypes, bookingTypes]);

  // Tag each booking type with isHidden based on usage permissions.
  // Only booking types where the user has FULL ACCESS are shown in the
  // Add Booking dropdown. Read-only types are hidden from the dropdown
  // but their bookings remain visible on the timeline in view-only mode.
  const permittedBookingTypes = useMemo(() => {
    if (!effectiveBookingTypes) return [];
    // Still loading or user not resolved — show all
    if (bookingTypeUsages == null || !userResolved) return effectiveBookingTypes.map(bt => ({ ...bt, isHidden: false }));

    const usageMap = new Map(bookingTypeUsages.map(u => [String(u.bookingTypeId), u]));
    return effectiveBookingTypes.map(bt => {
      const usage = usageMap.get(String(bt.id));
      // Hidden unless user has explicit full access
      const isHidden = !usage || !usage.isFullAccess;
      return { ...bt, isHidden };
    });
  }, [effectiveBookingTypes, bookingTypeUsages, userResolved]);

  // Use props data when available, fall back to context data. In live mode the
  // fallback is the live dataset so demo records are never offered as options.
  const isDemoMode = dataMode === 'demo';
  const resources = propResources || (isDemoMode ? contextResources : liveData.resources);
  const costCentres = propCostCentres || (isDemoMode ? contextCostCentres : liveData.costCentres);
  const clients = isDemoMode ? contextClients : liveData.clients;

  // Company context — used to guard project lookup when user has no company access
  const { currentCompany } = useCompany();

  // Use projectDetails prop (live data from ResourceCalendar) when available,
  // falling back to context projects for backward compatibility.
  // When the user has no company access (currentCompany is null) return an
  // empty list so the project lookup doesn't show unrelated projects.
  const effectiveProjects = useMemo(() => {
    if (!currentCompany) return [] as typeof projectDetails;
    if (propProjectDetails && propProjectDetails.length > 0) {
      return propProjectDetails as unknown as typeof projectDetails;
    }
    return isDemoMode ? projectDetails : liveData.projectDetails;
  }, [propProjectDetails, projectDetails, liveData.projectDetails, isDemoMode, currentCompany]);

  const isEditing = mode === 'edit' || !!editingBooking;

  // ── Fetch audit history from the backend API when opening an existing booking ──
  // Always fetch so we display the full persisted history, not just the
  // in-memory entries from the current session.
  const [apiAuditHistory, setApiAuditHistory] = useState<BookingAuditEntry[]>([]);
  useEffect(() => {
    if (!open || !editingBooking?.id) {
      setApiAuditHistory([]);
      return;
    }
    // Skip fetch for demo bookings (they use in-memory auditHistory)
    const bookingId = String(editingBooking.id);
    if (bookingId.startsWith('demo-')) return;

    let cancelled = false;
    fetchAuditHistory(bookingId).then(entries => {
      if (!cancelled) setApiAuditHistory(entries);
    });
    return () => { cancelled = true; };
  }, [open, editingBooking?.id]);

  // Stable references for ResourceRecommendationDialog — prevents engine re-running on every parent render
  // requiredSkills is already state — pass directly; the engine memoises internally by reference equality
  const stableEmptySkills = useMemo<string[]>(() => [], []);
  const stableProjects = useMemo(
    () => (effectiveProjects ?? []) as unknown as import('@/types').Project[],
    [effectiveProjects]
  );
  // Use passed existingBookings prop when available, fall back to context bookings
  const stableExistingBookings = useMemo(
    () => (propExistingBookings ?? []) as import('@/types').Booking[],
    [propExistingBookings]
  );

  // Form state
  const [bookingTypeId, setBookingTypeId] = useState<string>('');

  const selectedBookingTypeUsage = useMemo(() => {
    if (!bookingTypeId || !bookingTypeUsages?.length) return null;
    // Use string comparison so both numeric IDs (live mode) and string IDs
    // like 'bt-1' (demo mode) match correctly. Number('bt-1') === NaN which
    // never equals a string, causing every demo booking to appear read-only.
    return bookingTypeUsages.find(u => String(u.bookingTypeId) === String(bookingTypeId)) || null;
  }, [bookingTypeId, bookingTypeUsages]);

  // Role lacks the editBooking permission — an existing booking opens read-only.
  // If the role still has deleteBooking, the form is view-only but the Delete
  // action stays enabled ("delete-only" mode).
  const editPermissionDenied = isEditing && !canEditBooking();
  const deleteOnlyMode = editPermissionDenied && canDeleteBooking();

  // View-only when:
  //  1. user has explicit read-only permission (isReadOnly && !isFullAccess), OR
  //  2. editing an existing booking whose type the user has no usage entry for
  //     (i.e. no permission at all — the booking is visible on the timeline
  //      but the user must not be able to modify it), OR
  //  3. the role lacks the editBooking permission
  const isBookingReadOnly = (() => {
    // Role-level edit permission denied — separate concern, never bypassed by
    // the override-read-only permission.
    if (editPermissionDenied) return true;
    // Users with the "Override Read-Only Bookings" permission bypass the
    // per-booking-type read-only restrictions below, matching backend behavior
    // (see canOverrideReadOnly in BookingsController.Update).
    if (canOverrideReadOnly()) return false;
    // Explicit read-only usage
    if (selectedBookingTypeUsage?.isReadOnly === true && !selectedBookingTypeUsage?.isFullAccess) return true;
    // Editing an existing booking and usages have loaded — no matching usage means no permission
    if (isEditing && bookingTypeId && bookingTypeUsages != null && userResolved) {
      if (!selectedBookingTypeUsage) return true;
      if (!selectedBookingTypeUsage.isFullAccess) return true;
    }
    return false;
  })();
  const resolvedDefaultStatus = (dataMode === 'live' && settings?.timelineDefaultBookingStatus
    ? settings.timelineDefaultBookingStatus
    : 'confirmed') as BookingStatus;
  const [status, setStatus] = useState<BookingStatus>(resolvedDefaultStatus);
  const [colour, setColour] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>(
    preselectedDate || initialStartDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    preselectedEndDate || initialEndDate
  );
  // Hourly bookings default their From/To times to the business hours configured
  // in Settings → General → Defaults.
  const businessHoursRange = useMemo(() => ({
    start: formatHourAsTime(settings?.businessHoursStart, '09:00'),
    end: formatHourAsTime(settings?.businessHoursEnd, '17:00'),
  }), [settings?.businessHoursStart, settings?.businessHoursEnd]);

  const [startTime, setStartTime] = useState<string>(preselectedStartTime || businessHoursRange.start);
  const [endTime, setEndTime] = useState<string>(preselectedEndTime || businessHoursRange.end);
  const [durationType, setDurationType] = useState<DurationType>('full-day');
  const [startDateOpen, setStartDateOpen] = useState(false);

  // Selecting Hourly seeds From/To with the configured business hours, unless the
  // times were supplied by the caller (e.g. a timeline drag) or belong to an
  // hourly booking being edited.
  const handleDurationTypeChange = useCallback((value: DurationType) => {
    setDurationType(value);
    if (value !== 'hourly') return;
    if (preselectedStartTime || preselectedEndTime) return;
    if (editingBooking?.durationType === 'hourly') return;
    setStartTime(businessHoursRange.start);
    setEndTime(businessHoursRange.end);
    setEndDate(startDate);
  }, [businessHoursRange, preselectedStartTime, preselectedEndTime, editingBooking?.durationType, startDate]);

  // Real utilisation calculator for the resource picker. Delegates to the
  // canonical calculateResourceUtilization so the percentage shown here matches
  // the Resources page and heatmap: it respects each resource's working-hours
  // profile (skipping non-working days), booking duration types (full/half-day
  // vs hourly) and excludes cancelled bookings — rather than dividing raw
  // wall-clock hours by calendar days.  It covers the date range currently
  // selected in the form and sums every booking the resource already holds in
  // that range, so an over-booked resource reports above 100%.
  const calculateUtilization = useCallback((resourceId: string): number => {
    if (!stableExistingBookings.length) return 0;
    const workingProfile = settings?.globalWorkingProfile;
    if (!workingProfile) return 0;
    const sd = startDate || preselectedDate || initialStartDate;
    const ed = endDate || preselectedEndDate || initialEndDate || sd;
    if (!sd) return 0;
    const resource = resources.find(r => String(r.id) === String(resourceId));
    if (!resource) return 0;
    return calculateResourceUtilization(
      resourceId,
      stableExistingBookings,
      startOfDay(sd),
      endOfDay(ed || sd),
      resource,
      workingProfile,
      undefined,
      undefined,
      undefined,
      { includeOverbooking: true },
    );
  }, [stableExistingBookings, startDate, endDate, preselectedDate, initialStartDate, preselectedEndDate, initialEndDate, resources, settings?.globalWorkingProfile]);

  // ── Accordion expand/collapse all ──────────────────────────────────────────
  const ALL_ACCORDION_VALUES = [
    'schedule',
    'resources',
    'project-details',
    'additional-details',
    'address-contact',
    'booking-history',
    'project-notes',
    'custom-tab',
  ] as const;
  const [accordionValue, setAccordionValue] = useState<string[]>([...ALL_ACCORDION_VALUES]);
  const allExpanded = accordionValue.length === ALL_ACCORDION_VALUES.length;
  const toggleAllAccordions = () => {
    setAccordionValue(allExpanded ? [] : [...ALL_ACCORDION_VALUES]);
  };
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>(
    preselectedResource ? [String(preselectedResource.id)] :
    initialResourceId ? [initialResourceId] : []
  );
  const [projectId, setProjectId] = useState<string>('');
  const [costCentreId, setCostCentreId] = useState<string>('');
  const [costCentreTask, setCostCentreTask] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [details, setDetails] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [addressLine2, setAddressLine2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [county, setCounty] = useState<string>('');
  const [postcode, setPostcode] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [customerNotes, setCustomerNotes] = useState<string>('');
  const [projectNotes, setProjectNotes] = useState<string>('');
  const [bookingTimezone, setBookingTimezone] = useState<string>('');

  // User-defined fields state
  const [userChar, setUserChar] = useState<Record<number, string>>({});
  const [userNumber, setUserNumber] = useState<Record<number, string>>({});
  const [userFlag, setUserFlag] = useState<Record<number, boolean>>({});
  const [userDate, setUserDate] = useState<Record<number, string>>({});
  const [userNotes, setUserNotes] = useState<Record<number, string>>({});
  const [userTime, setUserTime] = useState<Record<number, string>>({});

  // Track whether the modal has finished initial setup so we can
  // clear selected resources only on SUBSEQUENT date changes (user action),
  // not during the initial render / preselection flow.
  const dateChangeCountRef = useRef(0);
  useEffect(() => {
    if (!open) {
      dateChangeCountRef.current = 0;
      return;
    }
    // Skip the very first change (initial value set during mount / preselection)
    if (dateChangeCountRef.current < 2) {
      dateChangeCountRef.current += 1;
      return;
    }
    // When editing, the main init effect re-runs as effectiveProjects /
    // clients load and each re-run creates new Date objects that bump this
    // counter past the threshold.  Never auto-clear resources during edit
    // — the booking already has resources assigned.
    if (editingBooking) {
      return;
    }
    // When a resource was preselected (e.g. from a right-click context menu
    // on a specific resource row), do not clear it on date change — the user
    // explicitly chose that resource and changing the date should not discard it.
    if (preselectedResource) {
      return;
    }
    // User actively changed dates — clear selected resources so they
    // can re-select based on the new date range.
    setSelectedResourceIds([]);
  }, [open, startDate, endDate, editingBooking, preselectedResource]);

  const pickerResources = useMemo(
    () => filterResourcesByWorkingDays(
      getResourcePickerOptions(resources, dataMode === 'live'),
      startDate,
      endDate,
      settings?.globalWorkingProfile,
      selectedResourceIds,
    ),
    [resources, dataMode, startDate, endDate, settings?.globalWorkingProfile, selectedResourceIds],
  );

  const recommendationResources = useMemo(
    () => pickerResources.filter((resource) => String(resource.id) !== UNALLOCATED_RESOURCE_ID),
    [pickerResources],
  );

  const handleResourceSelect = useCallback((nextIds: string[]) => {
    setSelectedResourceIds((previousIds) => normalizeResourceSelection(nextIds, previousIds));
  }, []);

  // Validation feedback state
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ── Real-time resource conflict detection ───────────────────────────────────
  // Detects existing bookings for the currently selected resources that fall on
  // any of the same calendar date(s) as the proposed booking — regardless of
  // whether the times overlap. This ensures the user sees a warning whenever a
  // resource already has bookings on the selected date (full-day, AM, PM, or
  // hourly).
  //
  // Date comparisons use YYYY-MM-DD strings extracted directly from ISO strings
  // (via regex) or from Date local components. This avoids timezone shifts that
  // occur when `new Date(isoString)` converts a UTC string to local time, which
  // can move the calendar day forward or backward depending on the user's offset.
  const detectedConflicts = useMemo<ConflictDetail[]>(() => {
    if (!startDate || !endDate || selectedResourceIds.length === 0) return [];
    if (!propExistingBookings || propExistingBookings.length === 0) return [];

    // ── Date-key helper ─────────────────────────────────────────────────
    const dateToKey = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // ── Proposed date range (from form state — already local dates) ────
    const pStart = dateToKey(startDate);
    let pEnd = dateToKey(endDate);
    // Guard: if endDate < startDate (e.g. edit-init walk-back for a
    // single-day booking stored as start === end at midnight), treat as
    // same-day range.
    if (pEnd < pStart) pEnd = pStart;

    // Build the proposed booking's day-segments with the shared overlap
    // primitive so the banner honours the real rules: full-day conflicts
    // with anything, morning only clashes with morning (not afternoon),
    // and custom/hourly bookings clash only on true time-interval overlap.
    const proposedForSegments = (
      durationType === 'hourly'
        ? { durationType: 'hourly', startTime: `${pStart}T${startTime || '00:00'}:00`, endTime: `${pEnd}T${endTime || '00:00'}:00` }
        : durationType === 'half-day-am'
          ? { durationType: 'half-day', halfDayPeriod: 'am', startTime: `${pStart}T00:00:00`, endTime: `${pStart}T11:59:00` }
          : durationType === 'half-day-pm'
            ? { durationType: 'half-day', halfDayPeriod: 'pm', startTime: `${pStart}T12:00:00`, endTime: `${pStart}T23:59:00` }
            : { durationType: 'full-day', startTime: `${pStart}T00:00:00`, endTime: `${pEnd}T23:59:59.999` }
    ) as Booking;
    const proposedSegments = getBookingSegments(proposedForSegments);

    const conflicts: ConflictDetail[] = [];

    for (const existingBooking of propExistingBookings) {
      // Skip the booking being edited (don't conflict with itself)
      if (editingBooking && String(existingBooking.id) === String(editingBooking.id)) continue;
      // Skip sibling bookings of a multi-resource booking being edited —
      // these share the same time window and their resourceId is part of the
      // editing booking's resourceIds group, so they are not real conflicts.
      if (editingBooking && editingBooking.resourceIds && editingBooking.resourceIds.length > 1 &&
          existingBooking.resourceId && editingBooking.resourceIds.map(String).includes(String(existingBooking.resourceId)) &&
          existingBooking.startTime === editingBooking.startTime &&
          existingBooking.endTime === editingBooking.endTime) {
        continue;
      }
      // Skip cancelled bookings
      if (existingBooking.status === 'cancelled') continue;

      // Determine the resource IDs of the existing booking
      const existingResourceIds: string[] = existingBooking.resourceIds
        ? existingBooking.resourceIds.map(String)
        : existingBooking.resourceId
          ? [String(existingBooking.resourceId)]
          : [];

      // Skip unallocated placeholder bookings entirely — not a real resource
      if (existingResourceIds.every(id => id === 'unallocated')) continue;

      // Find which of our selected resources overlap, excluding the unallocated placeholder
      const sharedResourceIds = selectedResourceIds.filter(
        id => id !== 'unallocated' && existingResourceIds.includes(id)
      );
      if (sharedResourceIds.length === 0) continue;

      // ── Overlap check (timezone-safe, honours AM/PM & hourly rules) ────
      // Two bookings only conflict when their minute-of-day intervals
      // overlap on a shared calendar day.
      const existingSegments = getBookingSegments(existingBooking);
      const overlaps = proposedSegments.some(ps =>
        existingSegments.some(
          es => ps.dateKey === es.dateKey && ps.startMin < es.endMin && ps.endMin > es.startMin,
        ),
      );
      if (!overlaps) continue;

      // For the conflict banner display, parse actual times
      const overlapStart = new Date(existingBooking.startTime);
      const overlapEnd = new Date(existingBooking.endTime);

      for (const rId of sharedResourceIds) {
        // Deduplicate: skip if we already have this booking+resource pair
        const dedupKey = `${existingBooking.id}|${rId}`;
        if (conflicts.some(c => `${c.conflictingBookingId}|${c.resourceId}` === dedupKey)) continue;

        const resource = resources.find(r => String(r.id) === rId);
        conflicts.push({
          resourceId: rId,
          resourceName: resource?.name ?? `Resource ${rId}`,
          conflictingBookingId: existingBooking.id,
          conflictingProjectName: existingBooking.projectName || existingBooking.description || 'Untitled Booking',
          overlapStart,
          overlapEnd,
          conflictingStartTime: existingBooking.startTime,
          conflictingEndTime: existingBooking.endTime,
        });
      }
    }

    return conflicts;
  }, [
    startDate, endDate, durationType, startTime, endTime,
    selectedResourceIds, propExistingBookings, editingBooking, resources,
  ]);

  // When editing from a specific resource row on the timeline, only show
  // conflicts related to that resource — not conflicts from other resources
  // that happen to be part of the same multi-resource booking.
  const filteredConflicts = useMemo<ConflictDetail[]>(() => {
    if (!editContextResourceId || !isEditing) return detectedConflicts;
    const ctxId = String(editContextResourceId);
    const filtered = detectedConflicts.filter(c => String(c.resourceId) === ctxId);
    return filtered;
  }, [detectedConflicts, editContextResourceId, isEditing]);

  // Dirty / leave-warning state
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);

  // Resource role field state
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [roleOpen, setRoleOpen] = useState(false);

  // Required skills field state — string[] of skill names (from PSA or manually added)
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const skillsScrollRef = React.useRef<HTMLDivElement>(null);

  // ── Per-resource assignment state (multi-resource layout) ─────────────────
  // Used when 2+ resources are selected. Each entry stores the role and
  // preferred skills for that individual resource, captured in local state only.
  // TODO: wire to backend — per-resource role and skills are not yet submitted.
  interface PerResourceAssignment {
    role: string;
    skills: string[];
    skillsOpen: boolean;
    roleOpen: boolean;
  }
  const [perResourceAssignments, setPerResourceAssignments] = useState<
    Map<string, PerResourceAssignment>
  >(new Map());

  // Ensure every selected resource has an entry in the map; clean up removed ones.
  useEffect(() => {
    setPerResourceAssignments((prev) => {
      const next = new Map(prev);
      // Add missing entries — seed role and skills from the resource's own profile defaults
      for (const id of selectedResourceIds) {
        if (!next.has(id)) {
          const resource = resources.find((r) => r.id === id);
          const defaultRole = resource?.role ?? '';
          const defaultSkills = (resource?.skills ?? []).map((s) => s.name);
          next.set(id, { role: defaultRole, skills: defaultSkills, skillsOpen: false, roleOpen: false });
        }
      }
      // Remove entries for resources that are no longer selected
      for (const id of Array.from(next.keys())) {
        if (!selectedResourceIds.includes(id)) {
          next.delete(id);
        }
      }
      return next;
    });
  }, [selectedResourceIds, resources]);

  // Update a single field on a per-resource assignment row
  const updatePerResourceAssignment = (
    resourceId: string,
    patch: Partial<PerResourceAssignment>
  ) => {
    setPerResourceAssignments((prev) => {
      const existing = prev.get(resourceId) ?? { role: '', skills: [], skillsOpen: false, roleOpen: false };
      const next = new Map(prev);
      next.set(resourceId, { ...existing, ...patch });
      return next;
    });
  };

  // When a role is selected in a per-resource row, pre-populate the skills
  // from the resources that hold that role. User can adjust afterwards.
  const handlePerResourceRoleChange = (resourceId: string, role: string) => {
    const defaultSkills = role
      ? Array.from(
          new Set(
            resources
              .filter((r) => r.role === role)
              .flatMap((r) => (r.skills ?? []).map((s) => s.name))
          )
        ).sort()
      : [];
    updatePerResourceAssignment(resourceId, { role, skills: defaultSkills });
  };

  // Toggle a skill tag on a per-resource row
  const togglePerResourceSkill = (resourceId: string, skillName: string) => {
    setPerResourceAssignments((prev) => {
      const existing = prev.get(resourceId) ?? { role: '', skills: [], skillsOpen: false, roleOpen: false };
      const skills = existing.skills.includes(skillName)
        ? existing.skills.filter((s) => s !== skillName)
        : [...existing.skills, skillName];
      const next = new Map(prev);
      next.set(resourceId, { ...existing, skills });
      return next;
    });
  };

  // Whether we are in "multi-resource" layout mode (2+ resources selected)
  const isMultiResourceMode = selectedResourceIds.length >= 2;

  // Resource recommendation dialog state
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false);
  const [showAllConflicts, setShowAllConflicts] = useState(false);

  // Tour integration — read active step to guard the Sheet from closing
  const { isActive: isTourActive, currentStepIndex, steps: tourSteps } = useTour();

  // Recurrence state
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>('none');
  const [recurrenceEndType, setRecurrenceEndType] = useState<RecurrenceEndType>('never');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | undefined>();
  const [recurrenceInterval, setRecurrenceInterval] = useState<string>('1');
  const [recurrenceOccurrences, setRecurrenceOccurrences] = useState<string>('10');
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = useState<string[]>([]);
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = useState<number | undefined>();
  const [recurrenceMonthlyType, setRecurrenceMonthlyType] = useState<'day-of-month' | 'relative-weekday'>('day-of-month');
  const [recurrenceRelativeWeekday, setRecurrenceRelativeWeekday] = useState<{ occurrence: 'first' | 'second' | 'third' | 'fourth' | 'last'; weekday: string } | undefined>();
  const [recurrenceSeriesStartDate, setRecurrenceSeriesStartDate] = useState<Date | undefined>();
  const [recurrenceDialogOpen, setRecurrenceDialogOpen] = useState(false);

  // Get selected booking type for field configuration
  // Check settings.bookingTypes first (updated after settings save), then fallback to context bookingTypes
  const selectedBookingType = useMemo(() => {
    if (!bookingTypeId) return null;
    const fromSettings = settings?.bookingTypes?.find(bt => String(bt.id) === bookingTypeId);
    if (fromSettings) return fromSettings;
    return bookingTypes?.find(bt => String(bt.id) === bookingTypeId) || null;
  }, [bookingTypeId, bookingTypes, settings?.bookingTypes]);

  // Field visibility helper based on booking type config
  // User-defined fields (User Char, User Number, etc.) default to hidden when not configured.
  // Core fields (project, customer, etc.) default to visible when not configured.
  const isUserDefinedField = useCallback((fieldName: string): boolean => {
    return /^User (Char|Number|Flag|Date|Time|Notes) \d+$/i.test(fieldName);
  }, []);

  const isFieldVisible = useCallback((fieldName: string): boolean => {
    if (!selectedBookingType?.fieldConfigurations) return !isUserDefinedField(fieldName);
    // Use case-insensitive matching to handle any casing differences from the API
    const fieldNameLower = fieldName.toLowerCase();
    const config = selectedBookingType.fieldConfigurations.find(
      (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === fieldNameLower
    );
    if (!config) return !isUserDefinedField(fieldName);
    if (!config.isVisible) return false;
    // Cascading visibility: Cost Centre requires Project, Cost Centre Task requires Cost Centre
    if (fieldNameLower === 'cost centre') {
      const projectConfig = selectedBookingType.fieldConfigurations.find(
        (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === 'project'
      );
      if (projectConfig && !projectConfig.isVisible) return false;
    }
    if (fieldNameLower === 'cost centre task') {
      const ccConfig = selectedBookingType.fieldConfigurations.find(
        (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === 'cost centre'
      );
      if (ccConfig && !ccConfig.isVisible) return false;
      const projectConfig = selectedBookingType.fieldConfigurations.find(
        (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === 'project'
      );
      if (projectConfig && !projectConfig.isVisible) return false;
    }
    return true;
  }, [selectedBookingType, isUserDefinedField]);

  // Field compulsory helper based on booking type config
  const isFieldCompulsory = useCallback((fieldName: string): boolean => {
    if (!selectedBookingType?.fieldConfigurations) return false;
    const fieldNameLower = fieldName.toLowerCase();
    const config = selectedBookingType.fieldConfigurations.find(
      (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === fieldNameLower
    );
    if (!config) return false;
    return config.isCompulsory;
  }, [selectedBookingType]);

  // Get custom label for a field from booking type config
  const getFieldLabel = useCallback((fieldName: string, defaultLabel: string): string => {
    if (!selectedBookingType?.fieldConfigurations) return defaultLabel;
    const fieldNameLower = fieldName.toLowerCase();
    const config = selectedBookingType.fieldConfigurations.find(
      (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === fieldNameLower
    );
    return config?.customName || defaultLabel;
  }, [selectedBookingType]);

  // Get custom placeholder for a field from booking type config.
  // Always derives the placeholder from the displayed label so the two stay in sync.
  const getFieldPlaceholder = useCallback((fieldName: string, defaultPlaceholder: string): string => {
    if (!selectedBookingType?.fieldConfigurations) return defaultPlaceholder;
    const fieldNameLower = fieldName.toLowerCase();
    const config = selectedBookingType.fieldConfigurations.find(
      (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === fieldNameLower
    );
    if (!config) return defaultPlaceholder;
    const label = config.customName || config.fieldName;
    if (['project', 'cost centre', 'customer', 'cost centre task', 'status', 'booking type', 'colour'].includes(fieldNameLower)) {
      return `Select ${label.toLowerCase()}`;
    }
    return `Enter ${label.toLowerCase()}`;
  }, [selectedBookingType]);

  // Tab visibility helper based on booking type tab config
  const isTabVisible = useCallback((tabName: string): boolean => {
    if (!selectedBookingType?.tabConfigurations) return true;
    const config = selectedBookingType.tabConfigurations.find(
      (tc: TabConfiguration) => tc.tabName === tabName
    );
    if (!config) return true;
    return config.isVisible;
  }, [selectedBookingType]);

  // Get custom label for a tab from booking type config
  const getTabLabel = useCallback((tabName: string, defaultLabel: string): string => {
    if (!selectedBookingType?.tabConfigurations) return defaultLabel;
    const config = selectedBookingType.tabConfigurations.find(
      (tc: TabConfiguration) => tc.tabName === tabName
    );
    return config?.customName || config?.defaultName || defaultLabel;
  }, [selectedBookingType]);

  // Get ordered field names for the project details section from booking type config
  // Role sits between Customer and Booking Reference — after project context is set, before resource is picked
  const orderedProjectFields = useMemo(() => {
    const defaultOrder = ['Project', 'Cost Centre', 'Cost Centre Task', 'Customer', 'Location', 'Booking Reference', 'Description', 'Details'];
    if (!selectedBookingType?.fieldOrder || selectedBookingType.fieldOrder.length === 0) return defaultOrder;
    // Only include fields that are relevant to the project details section (non-user-defined)
    const projectDetailFieldNames = new Set(defaultOrder.map(f => f.toLowerCase()));
    const ordered = selectedBookingType.fieldOrder.filter(
      f => projectDetailFieldNames.has(f.toLowerCase())
    );
    // Append any default fields not in the order
    for (const f of defaultOrder) {
      if (!ordered.some(o => o.toLowerCase() === f.toLowerCase())) {
        ordered.push(f);
      }
    }
    return ordered;
  }, [selectedBookingType]);

  // Derive unique roles from selected resources — used to populate the Role field
  const rolesFromSelectedResources = useMemo(() => {
    const selectedResObjs = resources.filter((r) =>
      selectedResourceIds.includes(String(r.id))
    );
    const roles = Array.from(new Set(selectedResObjs.map((r) => r.role).filter(Boolean)));
    return roles.sort();
  }, [resources, selectedResourceIds]);

  // All unique roles across all resources — shown when nothing is selected yet
  const allResourceRoles = useMemo(() => {
    const roles = Array.from(new Set(resources.map((r) => r.role).filter(Boolean)));
    return roles.sort();
  }, [resources]);

  // All unique skill names across all resources — used as autocomplete vocab for the skills field
  const allSkillNames = useMemo(() => {
    const names = new Set<string>();
    resources.forEach((r) => (r.skills ?? []).forEach((s) => names.add(s.name)));
    return Array.from(names).sort();
  }, [resources]);

  // Skills field helpers
  const addSkill = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || requiredSkills.includes(trimmed)) return;
    setRequiredSkills((prev) => [...prev, trimmed]);
  };
  const removeSkill = (name: string) =>
    setRequiredSkills((prev) => prev.filter((s) => s !== name));

  const toggleSkill = (name: string) => {
    if (requiredSkills.includes(name)) {
      removeSkill(name);
    } else {
      addSkill(name);
    }
  };

  // When the selected resources change:
  // 1. Auto-set the role if there is exactly one unique role across selected resources
  // 2. Clear the role if the current value is no longer valid for the selected resources
  useEffect(() => {
    if (rolesFromSelectedResources.length === 1) {
      // Exactly one distinct role — set it automatically so the user never has to look it up
      setSelectedRole(rolesFromSelectedResources[0]);
    } else if (
      selectedRole &&
      rolesFromSelectedResources.length > 0 &&
      !rolesFromSelectedResources.includes(selectedRole)
    ) {
      // Role no longer valid for the current resource selection — clear it
      setSelectedRole('');
    } else if (selectedResourceIds.length === 0) {
      // All resources removed — reset role so "Select Role" placeholder shows
      setSelectedRole('');
    }
  }, [rolesFromSelectedResources, selectedResourceIds]);

  // Get section order from booking type config
  // Natural flow: Schedule → Booking Details (project/role context) → Resources (picked last with full context)
  // sectionOrder uses: "datetime" = Schedule, "booking-details" = Project Details, "resources" = Resources
  const orderedSections = useMemo(() => {
    const defaultOrder = ['datetime', 'booking-details', 'resources'];
    if (!selectedBookingType?.sectionOrder || selectedBookingType.sectionOrder.length === 0) return defaultOrder;
    return selectedBookingType.sectionOrder;
  }, [selectedBookingType]);

  // Get tab order from booking type tab configurations
  const orderedTabs = useMemo(() => {
    const defaultOrder = ['address', 'history', 'project-notes', 'custom'];
    if (!selectedBookingType?.tabConfigurations || selectedBookingType.tabConfigurations.length === 0) return defaultOrder;
    // Use the order in tabConfigurations array (already sorted by wizard)
    const configured = selectedBookingType.tabConfigurations.map((tc: TabConfiguration) => tc.tabName);
    // Append any default tabs missing from the config (e.g. 'history' on older booking types)
    const configuredSet = new Set(configured);
    const missing = defaultOrder.filter(t => !configuredSet.has(t));
    return missing.length > 0 ? [...configured, ...missing] : configured;
  }, [selectedBookingType]);

  // Get status options from settings
  const statusOptions = useMemo(() => {
    if (settings?.statusConfigs && settings.statusConfigs.length > 0) {
      return settings.statusConfigs
        .filter(sc => sc.enabled)
        .map(sc => ({
          value: String(sc.id),
          label: sc.customText || sc.text,
          color: sc.color,
        }));
    }
    return DEFAULT_STATUS_CONFIGS
      .filter(d => d.enabled)
      .map(d => ({ value: d.id, label: d.text, color: d.color }));
  }, [settings?.statusConfigs]);

  // Get colour options from settings — use hex color as value so booking.color
  // stores the hex directly. This lets use-booking-color.ts match the override.
  const colourOptions = useMemo(() => {
    if (settings?.colorOverrides && settings.colorOverrides.length > 0) {
      return settings.colorOverrides
        .filter(co => co.enabled)
        .map(co => ({
          value: co.color,
          label: co.customText || co.text,
          color: co.color,
        }));
    }
    return [
      { value: '#3B82F6', label: 'Blue', color: '#3B82F6' },
      { value: '#10B981', label: 'Green', color: '#10B981' },
      { value: '#EF4444', label: 'Red', color: '#EF4444' },
      { value: '#F59E0B', label: 'Yellow', color: '#F59E0B' },
      { value: '#8B5CF6', label: 'Purple', color: '#8B5CF6' },
      { value: '#EC4899', label: 'Pink', color: '#EC4899' },
    ];
  }, [settings?.colorOverrides]);

  // Initialize form when editing
  useEffect(() => {
    if (editingBooking) {
      setBookingTypeId(editingBooking.bookingTypeId ? String(editingBooking.bookingTypeId) : '');
      setStatus(editingBooking.status || 'confirmed');
      setColour(editingBooking.color || '');

      const startDateTime = editingBooking.startTime ? new Date(editingBooking.startTime) : undefined;
      const endDateTime = editingBooking.endTime ? new Date(editingBooking.endTime) : undefined;

      // For non-hourly bookings, extract the date from the stored string to bypass
      // JavaScript Date timezone handling that can shift the date.
      // Also handles Bryntum's exclusive end date convention: if the stored endTime
      // is at midnight (T00:00:00), the actual last day is the previous day.
      const parseDateOnly = (dateStr: string, subtractDayIfMidnight: boolean = false): Date => {
        const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (m) {
          const year = parseInt(m[1]);
          const month = parseInt(m[2]) - 1;
          const day = parseInt(m[3]);
          const hour = parseInt(m[4]);
          const min = parseInt(m[5]);
          const sec = parseInt(m[6]);
          // If time is midnight (00:00:00), the date was stored from Bryntum's
          // exclusive end date. Subtract 1 day to get the inclusive last day.
          if (subtractDayIfMidnight && hour === 0 && min === 0 && sec === 0) {
            const d = new Date(year, month, day);
            d.setDate(d.getDate() - 1);
            return d;
          }
          return new Date(year, month, day);
        }
        return new Date(dateStr);
      };

      const isHourlyBooking = editingBooking.durationType === 'hourly';
      if (!isHourlyBooking) {
        setStartDate(editingBooking.startTime ? parseDateOnly(editingBooking.startTime) : undefined);
        setEndDate(editingBooking.endTime ? parseDateOnly(editingBooking.endTime, true) : undefined);
      } else {
        setStartDate(startDateTime);
        setEndDate(endDateTime);
      }

      if (startDateTime && endDateTime) {
        const sH = startDateTime.getHours().toString().padStart(2, '0');
        const sM = startDateTime.getMinutes().toString().padStart(2, '0');
        const eH = endDateTime.getHours().toString().padStart(2, '0');
        const eM = endDateTime.getMinutes().toString().padStart(2, '0');
        setStartTime(sH + ':' + sM);
        setEndTime(eH + ':' + eM);
      }

      let detectedDurationType: DurationType = editingBooking.durationType || 'full-day';
      if (detectedDurationType === 'half-day' && editingBooking.halfDayPeriod) {
        detectedDurationType = editingBooking.halfDayPeriod === 'am' ? 'half-day-am' : 'half-day-pm';
      } else if (detectedDurationType === 'half-day' && startDateTime) {
        const hour = startDateTime.getHours();
        detectedDurationType = hour < 12 ? 'half-day-am' : 'half-day-pm';
      }
      setDurationType(detectedDurationType);

      if (editingBooking.resourceIds && editingBooking.resourceIds.length > 0) {
        setSelectedResourceIds(editingBooking.resourceIds.map(String));
      } else if (editingBooking.resourceId) {
        setSelectedResourceIds([String(editingBooking.resourceId)]);
      } else {
        setSelectedResourceIds(dataMode === 'live' ? [UNALLOCATED_RESOURCE_ID] : []);
      }

      // Role and skills are initialised in a dedicated useEffect below
      // so they are NOT reset when effectiveProjects / clients change.

      setProjectId(editingBooking.projectId ? String(editingBooking.projectId) : '');
      setCostCentreId(editingBooking.costCentreId ? String(editingBooking.costCentreId) : '');
      setCostCentreTask(editingBooking.costCentreTask || '');
      // Resolve customer: prefer clientId (if it matches a known local client) >
      // project.clientId > project.clientName > customer name > raw string.
      // Open Demand bookings may carry an external EBProjects clientId that
      // doesn't correspond to a local client record — fall through to project-
      // based resolution in that case so the customer filter doesn't blank out
      // the project list.
      const bookingClientId = editingBooking.clientId != null ? String(editingBooking.clientId) : null;
      const knownClient = bookingClientId
        ? clients?.find(c => String(c.id) === bookingClientId)
        : null;

      if (bookingClientId && knownClient) {
        setCustomerId(bookingClientId);
      } else if (editingBooking.projectId) {
        // Auto-populate customer from the project's default client when editing
        const editProject = effectiveProjects?.find(p => String(p.id) === String(editingBooking.projectId));
        if (editProject && editProject.clientId != null) {
          setCustomerId(String(editProject.clientId));
        } else if (editProject && editProject.clientName) {
          // Fallback: match by project's client name when clientId is not set
          const matchedByProjectName = clients?.find(c => c.name === editProject.clientName);
          if (matchedByProjectName) {
            setCustomerId(String(matchedByProjectName.id));
          } else if (editingBooking.customer) {
            const matchedClient = clients?.find(c => c.name === editingBooking.customer);
            setCustomerId(matchedClient ? String(matchedClient.id) : editingBooking.customer);
          } else {
            setCustomerId('');
          }
        } else if (editingBooking.customer) {
          // Try to match customer name to a client record
          const matchedClient = clients?.find(c => c.name === editingBooking.customer);
          setCustomerId(matchedClient ? String(matchedClient.id) : editingBooking.customer);
        } else {
          setCustomerId('');
        }
      } else if (editingBooking.customer) {
        // No project — try to match customer name to a client record
        const matchedClient = clients?.find(c => c.name === editingBooking.customer);
        setCustomerId(matchedClient ? String(matchedClient.id) : editingBooking.customer);
      } else {
        setCustomerId('');
      }
      setReference(editingBooking.bookingReference || '');
      setDescription(editingBooking.description || editingBooking.details || '');
      setDetails(editingBooking.details || '');
      setAddress(editingBooking.location || '');
      // Populate address fields from resolved client (works in both demo and live mode)
      let resolvedClient = null;
      if (editingBooking.clientId) {
        resolvedClient = clients?.find(c => String(c.id) === String(editingBooking.clientId)) ?? null;
      }
      if (!resolvedClient && editingBooking.projectId) {
        const editProjectForAddr = effectiveProjects?.find(p => String(p.id) === String(editingBooking.projectId));
        if (editProjectForAddr?.clientId != null) {
          resolvedClient = clients?.find(c => String(c.id) === String(editProjectForAddr.clientId)) ?? null;
        }
        if (!resolvedClient && editProjectForAddr?.clientName) {
          resolvedClient = clients?.find(c => c.name === editProjectForAddr.clientName) ?? null;
        }
      }
      setAddress(resolvedClient?.streetAddress || editingBooking.location || '');
      setAddressLine2(resolvedClient?.addressLine2 || '');
      setCity(resolvedClient?.city || '');
      setCounty(resolvedClient?.county || '');
      setPostcode(resolvedClient?.postcode || '');
      setPhone(resolvedClient?.phoneNumber || resolvedClient?.mobileNumber || '');
      setEmail(resolvedClient?.email || '');
      setCustomerNotes(editingBooking.bookingNotes || '');
      setProjectNotes(editingBooking.taskDescription || '');
      setBookingTimezone(editingBooking.timezone || '');

      // Initialize user-defined fields
      setUserChar(editingBooking.userChar || {});
      setUserNumber(editingBooking.userNumber || {});
      setUserFlag(editingBooking.userFlag || {});
      setUserDate(editingBooking.userDate ? Object.fromEntries(
        Object.entries(editingBooking.userDate).map(([k, v]) => [k, typeof v === 'string' ? v : ''])
      ) : {});
      setUserNotes(editingBooking.userNotes || {});
      setUserTime(editingBooking.userTime || {});

      // Recurrence fields are populated separately via API fetch below
      // Reset them here so stale values from a previous edit don't linger
      if (!editingBooking.isRecurrenceParent && !editingBooking.recurrenceGroupId) {
        setRecurrencePattern('none');
        setRecurrenceEndType('never');
        setRecurrenceEndDate(undefined);
        setRecurrenceInterval('1');
        setRecurrenceOccurrences('10');
        setRecurrenceDaysOfWeek([]);
        setRecurrenceDayOfMonth(undefined);
        setRecurrenceMonthlyType('day-of-month');
        setRecurrenceRelativeWeekday(undefined);
      }
      // Form just loaded with data — not dirty yet
      setIsDirty(false);
    } else {
      // No editing booking — reset all fields to blank for create mode
      setBookingTypeId('');
      setStatus(resolvedDefaultStatus);
      setColour('');
      setStartDate(preselectedDate || initialStartDate);
      setEndDate(preselectedEndDate || initialEndDate);
      setStartTime(preselectedStartTime || businessHoursRange.start);
      setEndTime(preselectedEndTime || businessHoursRange.end);
      setDurationType('full-day');
      setSelectedResourceIds(
        preselectedResource ? [String(preselectedResource.id)] :
        initialResourceId ? [initialResourceId] : []
      );
      setProjectId('');
      setCostCentreId('');
      setCostCentreTask('');
      setCustomerId('');
      setReference('');
      setDescription('');
      setDetails('');
      setAddress('');
      setAddressLine2('');
      setCity('');
      setCounty('');
      setPostcode('');
      setPhone('');
      setEmail('');
      setCustomerNotes('');
      setProjectNotes('');
      setBookingTimezone(defaultTimezone || settings?.defaultTimezone || '');
      setUserChar({});
      setUserNumber({});
      setUserFlag({});
      setUserDate({});
      setUserNotes({});
      setUserTime({});
      setRecurrencePattern('none');
      setRecurrenceEndType('never');
      setRecurrenceEndDate(undefined);
      setRecurrenceInterval('1');
      setRecurrenceOccurrences('10');
      setRecurrenceDaysOfWeek([]);
      setRecurrenceDayOfMonth(undefined);
      setRecurrenceMonthlyType('day-of-month');
      setRecurrenceRelativeWeekday(undefined);
      setSkillsOpen(false);
      setRoleOpen(false);
      setIsDirty(false);
      // NOTE: requiredSkills and selectedRole are reset in the dedicated
      // skills/role useEffect below — do NOT reset them here.
    }
  }, [editingBooking, effectiveProjects, clients, dataMode]);

  // For a new booking, default the timezone to the one currently selected on the
  // timeline (falling back to the system default). Runs whenever the modal opens
  // so the latest timeline selection is reflected in the Add Booking screen.
  // A timezone carried in on preselected data (e.g. pasting a copied booking)
  // always wins so the pasted booking keeps the source booking's timezone.
  useEffect(() => {
    if (open && !editingBooking && !preselectedBookingData?.timezone) {
      setBookingTimezone(defaultTimezone || settings?.defaultTimezone || '');
    }
  }, [open, editingBooking, defaultTimezone, settings?.defaultTimezone, preselectedBookingData?.timezone]);

  // ── Skills & Role initialisation ──────────────────────────────────────
  // ALL requiredSkills / selectedRole state management lives here so it is
  // completely isolated from the main form-initialisation effect above.
  // The main effect depends on effectiveProjects / clients (for customer
  // resolution) and re-runs when those async data sources load — but it
  // must NEVER touch skills or role, otherwise a late-loading source would
  // wipe user selections.
  useEffect(() => {
    if (editingBooking) {
      // Restore role from the booking (if stored).
      if (editingBooking.requiredRole) {
        setSelectedRole(editingBooking.requiredRole);
      }

      // Restore required skills.
      // Defensive: handle both a real string[] and an accidentally-serialised
      // JSON string (e.g. '["React","Node.js"]') so the UI never silently
      // drops skills regardless of how the data arrived.
      let skills: string[] = [];
      const raw: unknown = editingBooking.requiredSkills;
      if (Array.isArray(raw) && raw.length > 0) {
        skills = raw as string[];
      } else if (typeof raw === 'string' && raw.length > 0) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) skills = parsed;
        } catch { /* ignore malformed JSON */ }
      }
      setRequiredSkills(skills);
    } else {
      // Create-mode / modal closed — reset skills & role.
      setRequiredSkills([]);
      setSelectedRole('');
    }
  }, [editingBooking]);

  // Fetch RecurrenceSettings from API when editing a recurring booking
  useEffect(() => {
    if (!editingBooking) return;
    const bookingId = parseInt(String(editingBooking.id));
    if (isNaN(bookingId)) return;
    // Only fetch if this is a recurring booking
    if (!editingBooking.isRecurrenceParent && !editingBooking.recurrenceGroupId) return;

    let cancelled = false;
    (async () => {
      try {
        // RecurrenceSettings are stored against the parent booking.
        // Try the current booking first; if not found, use recurrenceGroupId
        // to find the parent's settings via the group endpoint.
        let rs = await recurrenceSettingsApi.getByBookingId(bookingId).catch(() => null);
        if (!rs && editingBooking.recurrenceGroupId) {
          rs = await recurrenceSettingsApi.getByRecurrenceGroupId(editingBooking.recurrenceGroupId).catch(() => null);
        }
        if (!rs && editingBooking.parentBookingId) {
          rs = await recurrenceSettingsApi.getByBookingId(editingBooking.parentBookingId).catch(() => null);
        }
        if (cancelled) return;
        if (!rs) {
          // No RecurrenceSettings found — reset to defaults
          setRecurrencePattern('none');
          setRecurrenceEndType('never');
          setRecurrenceEndDate(undefined);
          setRecurrenceInterval('1');
          setRecurrenceOccurrences('10');
          setRecurrenceDaysOfWeek([]);
          setRecurrenceDayOfMonth(undefined);
          setRecurrenceMonthlyType('day-of-month');
          setRecurrenceRelativeWeekday(undefined);
          return;
        }
        // Map RecurrenceSettings API fields to modal state
        const pattern = (rs.pattern || 'none') as RecurrencePattern;
        setRecurrencePattern(pattern);
        setRecurrenceInterval(String(rs.interval || 1));
        setRecurrenceEndType((rs.endType || 'never') as RecurrenceEndType);
        setRecurrenceEndDate(rs.endDate ? new Date(rs.endDate) : undefined);
        setRecurrenceOccurrences(rs.endAfterOccurrences ? String(rs.endAfterOccurrences) : '10');

        // Weekly: daysOfWeek is stored as JSON array string e.g. '["monday","wednesday"]'
        if (pattern === 'weekly' && rs.daysOfWeek) {
          try {
            const parsed = JSON.parse(rs.daysOfWeek);
            if (Array.isArray(parsed)) {
              setRecurrenceDaysOfWeek(parsed);
            } else {
              setRecurrenceDaysOfWeek([]);
            }
          } catch {
            // If it's not JSON array, it might be a single weekday string (from relative weekday)
            setRecurrenceDaysOfWeek([]);
          }
        } else {
          setRecurrenceDaysOfWeek([]);
        }

        // Monthly: determine type from stored fields
        if (pattern === 'monthly') {
          if (rs.weekOfMonth && rs.daysOfWeek) {
            // Relative weekday pattern (e.g., "third Tuesday")
            setRecurrenceMonthlyType('relative-weekday');
            setRecurrenceDayOfMonth(undefined);
            const occurrenceMap: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
              1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'last'
            };
            setRecurrenceRelativeWeekday({
              occurrence: occurrenceMap[rs.weekOfMonth] || 'first',
              weekday: rs.daysOfWeek, // single weekday string for monthly
            });
          } else if (rs.dayOfMonth) {
            setRecurrenceMonthlyType('day-of-month');
            setRecurrenceDayOfMonth(rs.dayOfMonth);
            setRecurrenceRelativeWeekday(undefined);
          } else {
            setRecurrenceMonthlyType('day-of-month');
            setRecurrenceDayOfMonth(undefined);
            setRecurrenceRelativeWeekday(undefined);
          }
        } else {
          setRecurrenceDayOfMonth(undefined);
          setRecurrenceRelativeWeekday(undefined);
          setRecurrenceMonthlyType('day-of-month');
        }
      } catch (err) {
        console.error('Failed to fetch RecurrenceSettings for booking', bookingId, err);
        // If fetch fails (e.g., no settings exist), reset to defaults
        setRecurrencePattern('none');
        setRecurrenceEndType('never');
        setRecurrenceEndDate(undefined);
        setRecurrenceInterval('1');
        setRecurrenceOccurrences('10');
        setRecurrenceDaysOfWeek([]);
        setRecurrenceDayOfMonth(undefined);
        setRecurrenceMonthlyType('day-of-month');
        setRecurrenceRelativeWeekday(undefined);
      }
    })();
    return () => { cancelled = true; };
  }, [editingBooking]);

  // Sync startDate/endDate when preselectedDate props change (e.g. tour mock data set after mount)
  useEffect(() => {
    if (!editingBooking && preselectedDate) {
      setStartDate(preselectedDate);
    }
  }, [preselectedDate, editingBooking]);

  useEffect(() => {
    if (!editingBooking && preselectedEndDate) {
      setEndDate(preselectedEndDate);
    }
  }, [preselectedEndDate, editingBooking]);

  // Initialize from preselected data
  useEffect(() => {
    if (!editingBooking && preselectedBookingData) {
      if (preselectedBookingData.bookingTypeId) setBookingTypeId(String(preselectedBookingData.bookingTypeId));
      if (preselectedBookingData.durationType) setDurationType(preselectedBookingData.durationType);
      if (preselectedBookingData.status) setStatus(preselectedBookingData.status);
      if (preselectedBookingData.projectId) {
        const preProjectId = String(preselectedBookingData.projectId);
        setProjectId(preProjectId);
        // Auto-populate customer from the project's default client
        const preProject = effectiveProjects?.find(p => String(p.id) === preProjectId);
        if (preProject) {
          let resolvedClient = preProject.clientId != null
            ? clients?.find(c => String(c.id) === String(preProject.clientId))
            : preProject.clientName
              ? clients?.find(c => c.name === preProject.clientName)
              : undefined;
          if (resolvedClient) {
            const preClientId = String(resolvedClient.id);
            setCustomerId(preClientId);
            setAddress(resolvedClient.streetAddress || '');
            setAddressLine2(resolvedClient.addressLine2 || '');
            setCity(resolvedClient.city || '');
            setCounty(resolvedClient.county || '');
            setPostcode(resolvedClient.postcode || '');
            setPhone(resolvedClient.phoneNumber || resolvedClient.mobileNumber || '');
            setEmail(resolvedClient.email || '');
          }
        }
        // Auto-populate description from the pre-selected project only when
        // the description or details field is enabled for the current booking type.
        if (preProject && (isFieldVisible('Description') || isFieldVisible('Details'))) {
          if (settings?.descriptionDefault === 'from-project') {
            setDescription(preProject.name || '');
          } else if (settings?.descriptionDefault === 'from-project-description') {
            setDescription(preProject.projectDescription || preProject.description || '');
          } else {
            setDescription('');
          }
          setDetails('');
        }
      }
      // Pre-set customer directly when no project drives it (e.g. right-click
      // "Add Booking" from Customer view). A pre-selected project already
      // resolves the customer above, so only apply this when projectId is unset.
      if (!preselectedBookingData.projectId && preselectedBookingData.customer) {
        const preCustomerName = String(preselectedBookingData.customer);
        const matchedClient = clients?.find(c => c.name === preCustomerName);
        if (matchedClient) {
          setCustomerId(String(matchedClient.id));
          setAddress(matchedClient.streetAddress || '');
          setAddressLine2(matchedClient.addressLine2 || '');
          setCity(matchedClient.city || '');
          setCounty(matchedClient.county || '');
          setPostcode(matchedClient.postcode || '');
          setPhone(matchedClient.phoneNumber || matchedClient.mobileNumber || '');
          setEmail(matchedClient.email || '');
        } else {
          setCustomerId(preCustomerName);
        }
      }
      // Pre-set role from booking data (e.g. navigating from Project Staffing)
      if (preselectedBookingData.requiredRole) {
        setSelectedRole(preselectedBookingData.requiredRole);
      }
      // Pre-set description directly (e.g. from tour mock data)
      if (preselectedBookingData.description) {
        setDescription(preselectedBookingData.description);
      }
      // Pre-set timezone (e.g. pasting a copied booking) so the copied
      // booking's timezone is preserved instead of the timeline default.
      if (preselectedBookingData.timezone) {
        setBookingTimezone(preselectedBookingData.timezone);
      }
    }
  }, [editingBooking, preselectedBookingData, effectiveProjects, clients, settings?.descriptionDefault, isFieldVisible]);

  // Apply default booking status when opening in create mode for live data
  useEffect(() => {
    if (open && mode === 'create' && !editingBooking && dataMode === 'live' && settings?.timelineDefaultBookingStatus) {
      setStatus(settings.timelineDefaultBookingStatus as BookingStatus);
    }
  }, [open, mode, editingBooking, dataMode, settings?.timelineDefaultBookingStatus]);

  // Initialize form from preselected values when modal opens
  useEffect(() => {
    if (open && !editingBooking) {
      // Set resource from preselected and auto-populate their role
      if (preselectedResource) {
        setSelectedResourceIds([String(preselectedResource.id)]);
        if (preselectedResource.role) {
          setSelectedRole(preselectedResource.role);
        }
      }
      // Set dates from preselected
      if (preselectedDate) {
        setStartDate(preselectedDate);
      }
      if (preselectedEndDate) {
        setEndDate(preselectedEndDate);
      } else if (preselectedDate) {
        setEndDate(preselectedDate);
      }
      // Set times from preselected
      if (preselectedStartTime) {
        setStartTime(preselectedStartTime);
      }
      if (preselectedEndTime) {
        setEndTime(preselectedEndTime);
      }
    }
  }, [open, editingBooking, preselectedResource, preselectedDate, preselectedEndDate, preselectedStartTime, preselectedEndTime]);

  // Set default booking type when opening in create mode, or re-evaluate
  // when permitted types change (e.g. after user permissions load) so the
  // selected type is always one the logged-in user has access to.
  useEffect(() => {
    if (open && mode === 'create' && !editingBooking && permittedBookingTypes && permittedBookingTypes.length > 0) {
      // If a booking type is already selected and still available, keep it
      if (bookingTypeId) {
        const current = permittedBookingTypes.find(bt => String(bt.id) === bookingTypeId);
        if (current && current.active && !current.isHidden) return;
      }
      // Pick the configured company default, or fall back to the first
      // available booking type for the logged-in user
      const defaultId = settings?.timelineDefaultBookingType || settings?.defaultBookingTypeId;
      const defaultType = defaultId
        ? permittedBookingTypes.find(bt => String(bt.id) === String(defaultId) && bt.active && !bt.isHidden)
        : null;
      const fallbackType = defaultType || permittedBookingTypes.find(bt => bt.active && !bt.isHidden) || permittedBookingTypes.find(bt => !bt.isHidden);
      if (fallbackType) {
        setBookingTypeId(String(fallbackType.id));
      }
    }
  }, [open, mode, editingBooking, permittedBookingTypes, bookingTypeId, settings]);

  // Reset form when panel closes
  useEffect(() => {
    if (!open) {
      setBookingTypeId('');
      setStatus(resolvedDefaultStatus);
      setColour('');
      setStartDate(preselectedDate || initialStartDate);
      setEndDate(preselectedEndDate || initialEndDate);
      setStartTime(preselectedStartTime || businessHoursRange.start);
      setEndTime(preselectedEndTime || businessHoursRange.end);
      setDurationType('full-day');
      // durationValue reset handled by durationType change
      setSelectedResourceIds(
        preselectedResource ? [String(preselectedResource.id)] :
        initialResourceId ? [initialResourceId] : []
      );
      setProjectId('');
      setCostCentreId('');
      setCostCentreTask('');
      setCustomerId('');
      setReference('');
      setDescription('');
      setDetails('');
      setAddress('');
      setAddressLine2('');
      setCity('');
      setCounty('');
      setPostcode('');
      setPhone('');
      setEmail('');
      setCustomerNotes('');
      setProjectNotes('');
      setBookingTimezone(defaultTimezone || settings?.defaultTimezone || '');
      setRecurrencePattern('none');
      setRecurrenceEndType('never');
      setRecurrenceEndDate(undefined);
      setRecurrenceInterval('1');
      setUserChar({});
      setUserNumber({});
      setUserFlag({});
      setUserDate({});
      setUserNotes({});
      setUserTime({});
      setIsDirty(false);
      setShowLeaveWarning(false);
    }
  }, [open, preselectedDate, preselectedEndDate, preselectedStartTime, preselectedEndTime, preselectedResource, initialStartDate, initialEndDate, initialResourceId]);

  // === Cascading field logic ===

  // Filter projects by selected customer
  const filteredProjects = useMemo(() => {
    if (!effectiveProjects) return [];

    let filtered = effectiveProjects;

    // Apply customer filter (if customer selected)
    if (customerId) {
      const selectedClient = clients?.find(c => String(c.id) === String(customerId));
      filtered = filtered.filter(p => {
        if (p.clientId != null && String(p.clientId) === String(customerId)) return true;
        if (selectedClient && p.clientName && p.clientName === selectedClient.name) return true;
        return false;
      });
    }

    // Apply booking type project filter (if configured)
    if (selectedBookingType) {
      const projectField = selectedBookingType.fieldConfigurations?.find(
        (f: FieldConfiguration) => f.fieldName?.toLowerCase() === 'project'
      );
      if (projectField?.filterConfig?.enabled) {
        filtered = filtered.filter(project =>
          evaluateFilter(project as unknown as Record<string, unknown>, projectField.filterConfig!)
        );
      }
    }

    // Always keep the currently-selected project available so changing the
    // customer never hides / clears the chosen project.
    if (projectId && !filtered.some(p => String(p.id) === String(projectId))) {
      const selected = effectiveProjects.find(p => String(p.id) === String(projectId));
      if (selected) filtered = [selected, ...filtered];
    }

    return filtered;
  }, [effectiveProjects, customerId, clients, selectedBookingType, projectId]);

  // Get cost centres for the selected project.
  // Primary: use the nested costCentres array on the project (populated in SchedulerContext
  // via projectCode → billingCode matching, same pattern as EBTimeEntry).
  // Fallback: filter flat array by projectCode matching project's billingCode.
  // When no project is selected, show all cost centres.
  const filteredCostCentres = useMemo(() => {
    const allCCs = costCentres || [];
    if (!projectId) return allCCs;
    const selectedProject = effectiveProjects?.find(p => String(p.id) === String(projectId));
    const nested = selectedProject?.costCentres ?? [];
    if (nested.length > 0) return nested;
    // Fallback: filter by projectCode matching project's billingCode
    if (selectedProject?.billingCode) {
      const byCode = allCCs.filter(cc =>
        cc.projectCode != null && cc.projectCode === selectedProject.billingCode
      );
      if (byCode.length > 0) return byCode;
    }
    // No matching CCs for this project — return empty instead of all CCs
    return [];
  }, [costCentres, projectId, effectiveProjects]);

  // Resolve the local Client for a project using every client signal the
  // project carries. Preference order:
  //   1. scalar clientId (already resolved to a local client id / code)
  //   2. clientCodes CSV  → Client.code
  //   3. clientIds CSV    → Client.code / Client.id
  //   4. clientNames CSV / scalar clientName → Client.name
  // Returns the matched Client, or null when nothing matches.
  const resolveClientFromProject = useCallback((project: ProjectDetails): Client | null => {
    if (!clients || clients.length === 0) return null;

    const firstCsvValue = (csv?: string | null): string =>
      csv ? (csv.split(',').map(s => s.trim()).find(Boolean) ?? '') : '';

    if (project.clientId != null) {
      const idStr = String(project.clientId);
      const byId = clients.find(
        c => String(c.id) === idStr || (c.code != null && String(c.code) === idStr)
      );
      if (byId) return byId;
    }

    const firstCode = firstCsvValue(project.clientCodes);
    if (firstCode) {
      const byCode = clients.find(
        c => c.code != null && String(c.code).toLowerCase() === firstCode.toLowerCase()
      );
      if (byCode) return byCode;
    }

    const firstExternalId = firstCsvValue(project.clientIds);
    if (firstExternalId) {
      const byExternal = clients.find(
        c => String(c.id) === firstExternalId ||
          (c.code != null && String(c.code).toLowerCase() === firstExternalId.toLowerCase())
      );
      if (byExternal) return byExternal;
    }

    const firstName = firstCsvValue(project.clientNames) || project.clientName;
    if (firstName) {
      const byName = clients.find(
        c => c.name != null && c.name.toLowerCase() === firstName.toLowerCase()
      );
      if (byName) return byName;
    }

    return null;
  }, [clients]);

  // Helper to populate address & contact fields from a customer/client
  const populateAddressFromCustomer = useCallback((clientId: string) => {
    if (!clientId) {
      setAddress('');
      setAddressLine2('');
      setCity('');
      setCounty('');
      setPostcode('');
      setPhone('');
      setEmail('');
      return;
    }
    const client = clients?.find(c => String(c.id) === clientId);
    if (client) {
      setAddress(client.streetAddress || '');
      setAddressLine2(client.addressLine2 || '');
      setCity(client.city || '');
      setCounty(client.county || '');
      setPostcode(client.postcode || '');
      setPhone(client.phoneNumber || client.mobileNumber || '');
      setEmail(client.email || '');
    }
  }, [clients]);

  // Failsafe: auto-populate customer from project when modal opens with a pre-selected
  // project but customer is empty. This catches edge cases where the preselectedBookingData
  // effect sets projectId but customerId doesn't persist due to React effect ordering.
  useEffect(() => {
    if (open && !editingBooking && projectId && !customerId) {
      const project = effectiveProjects?.find(p => String(p.id) === projectId);
      if (project) {
        const matchedClient = resolveClientFromProject(project);
        if (matchedClient) {
          const clientId = String(matchedClient.id);
          setCustomerId(clientId);
          populateAddressFromCustomer(clientId);
        } else if (project.clientId != null) {
          const clientId = String(project.clientId);
          setCustomerId(clientId);
          populateAddressFromCustomer(clientId);
        }
      }
    }
  }, [open, editingBooking, projectId, customerId, effectiveProjects, resolveClientFromProject, populateAddressFromCustomer]);

  // Handle project change with cascading updates
  const handleProjectChange = useCallback((newProjectId: string) => {
    setProjectId(newProjectId);

    if (!newProjectId) {
      // Clearing the project - also clear cost centre, cost centre task, customer, and description
      setCostCentreId('');
      setCostCentreTask('');
      setCustomerId('');
      setDescription('');
      setDetails('');
      populateAddressFromCustomer('');
    } else {
      // A project was selected - auto-populate customer from project's client.
      // Resolve against every client signal the project carries
      // (scalar clientId, clientCodes / clientIds / clientNames CSVs) so the
      // Client field plus address, city, postcode, phone and email get set.
      const selectedProject = effectiveProjects?.find(p => String(p.id) === newProjectId);
      if (selectedProject) {
        const matchedClient = resolveClientFromProject(selectedProject);
        if (matchedClient) {
          const newCustomerId = String(matchedClient.id);
          setCustomerId(newCustomerId);
          populateAddressFromCustomer(newCustomerId);
        } else if (selectedProject.clientId != null) {
          // No local client matched — keep the raw client id so the field
          // isn't left blank (address stays empty as before).
          const clientIdStr = String(selectedProject.clientId);
          setCustomerId(clientIdStr);
          populateAddressFromCustomer(clientIdStr);
        }
      }
      // Clear cost centre and cost centre task when project changes (new project may have different cost centres)
      setCostCentreId('');
      setCostCentreTask('');

      // Auto-populate description from the selected project only when
      // the description or details field is enabled for the current booking type.
      if (selectedProject && (isFieldVisible('Description') || isFieldVisible('Details'))) {
        if (settings?.descriptionDefault === 'from-project') {
          setDescription(selectedProject.name || '');
        } else if (settings?.descriptionDefault === 'from-project-description') {
          setDescription(selectedProject.projectDescription || selectedProject.description || '');
        } else {
          setDescription('');
        }
        setDetails('');
      }
    }
  }, [effectiveProjects, resolveClientFromProject, populateAddressFromCustomer, settings?.descriptionDefault, isFieldVisible]);

  // Handle customer change - filter projects will update via useMemo
  const handleCustomerChange = useCallback((newCustomerId: string) => {
    setCustomerId(newCustomerId);
    // Populate address & contact from the selected customer
    populateAddressFromCustomer(newCustomerId);
    // Keep the currently selected project untouched when the customer changes,
    // even if the project belongs to a different client.
  }, [populateAddressFromCustomer]);

  const handleRecurrenceRemove = () => {
    setRecurrencePattern('none');
    setRecurrenceEndType('never');
    setRecurrenceEndDate(undefined);
    setRecurrenceInterval('1');
    setRecurrenceOccurrences('10');
    setRecurrenceDaysOfWeek([]);
    setRecurrenceDayOfMonth(undefined);
    setRecurrenceMonthlyType('day-of-month');
    setRecurrenceRelativeWeekday(undefined);
  };

  const getRecurrenceSummaryText = (): string => {
    const interval = parseInt(recurrenceInterval) || 1;
    const DAYS_ORDER = [
      { value: 'monday', short: 'Mon' },
      { value: 'tuesday', short: 'Tue' },
      { value: 'wednesday', short: 'Wed' },
      { value: 'thursday', short: 'Thu' },
      { value: 'friday', short: 'Fri' },
      { value: 'saturday', short: 'Sat' },
      { value: 'sunday', short: 'Sun' },
    ];
    let pattern = '';

    if (recurrencePattern === 'daily') {
      pattern = interval === 1 ? 'Daily' : `Every ${interval} days`;
    } else if (recurrencePattern === 'weekly') {
      const ref = recurrenceSeriesStartDate ?? startDate;
      const dayNames = recurrenceDaysOfWeek.length > 0
        ? [...recurrenceDaysOfWeek]
            .sort((a, b) => DAYS_ORDER.findIndex(d => d.value === a) - DAYS_ORDER.findIndex(d => d.value === b))
            .map(d => DAYS_ORDER.find(x => x.value === d)?.short)
            .join(', ')
        : ref
          ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][getDay(ref)].slice(0, 3)
          : '';
      pattern = interval === 1
        ? `Weekly on ${dayNames}`
        : `Every ${interval} weeks on ${dayNames}`;
    } else if (recurrencePattern === 'monthly') {
      if (recurrenceMonthlyType === 'day-of-month') {
        const ref = recurrenceSeriesStartDate ?? startDate;
        const day = recurrenceDayOfMonth ?? (ref ? getDate(ref) : undefined);
        pattern = interval === 1
          ? `Monthly on day ${day ?? ''}`
          : `Every ${interval} months on day ${day ?? ''}`;
      } else {
        const ref = recurrenceSeriesStartDate ?? startDate;
        const weekday = recurrenceRelativeWeekday?.weekday
          ?? (ref ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][getDay(ref)] : 'Monday');
        const ord = recurrenceRelativeWeekday?.occurrence
          ?? (ref ? (() => {
            const dayOfMonth = getDate(ref);
            const lastDay = new Date(ref.getFullYear(), ref.getMonth() + 1, 0).getDate();
            if (dayOfMonth + 7 > lastDay) return 'last';
            const week = Math.ceil(dayOfMonth / 7);
            return (['first', 'second', 'third', 'fourth'] as const)[week - 1] ?? 'first';
          })() : 'first');
        pattern = interval === 1
          ? `Monthly on the ${ord} ${weekday}`
          : `Every ${interval} months on the ${ord} ${weekday}`;
      }
    }

    const seriesRef = recurrenceSeriesStartDate ?? startDate;
    const from = seriesRef ? ` \u00b7 from ${format(seriesRef, 'dd MMM yyyy')}` : '';
    const until = recurrenceEndDate ? ` \u00b7 until ${format(recurrenceEndDate, 'dd MMM yyyy')}` : '';
    return `${pattern}${from}${until}`;
  };

  // Mark the form as dirty whenever the user changes anything
  const markDirty = useCallback(() => setIsDirty(true), []);

  // Watch all form fields — if the form is open and any value changes, mark dirty.
  // We use a JSON-serialised snapshot so we only trigger on real value changes.
  const formSnapshot = useMemo(() => JSON.stringify({
    bookingTypeId, status, colour, startDate, endDate, startTime, endTime,
    durationType, selectedResourceIds, projectId, costCentreId, customerId,
    reference, description, details, address, city, postcode, phone, email,
    customerNotes, projectNotes, bookingTimezone,
    userChar, userNumber, userFlag, userDate, userNotes, userTime,
    recurrencePattern, recurrenceEndType, recurrenceEndDate, recurrenceInterval,
    recurrenceOccurrences, recurrenceDaysOfWeek, recurrenceDayOfMonth,
    recurrenceMonthlyType, recurrenceRelativeWeekday,
  }), [bookingTypeId, status, colour, startDate, endDate, startTime, endTime,
    durationType, selectedResourceIds, projectId, costCentreId, customerId,
    reference, description, details, address, city, postcode, phone, email,
    customerNotes, projectNotes, bookingTimezone,
    userChar, userNumber, userFlag, userDate, userNotes, userTime,
    recurrencePattern, recurrenceEndType, recurrenceEndDate, recurrenceInterval,
    recurrenceOccurrences, recurrenceDaysOfWeek, recurrenceDayOfMonth,
    recurrenceMonthlyType, recurrenceRelativeWeekday]);

  const initialSnapshotRef = React.useRef<string | null>(null);

  const baselineReadyRef = React.useRef(false);

  useEffect(() => {
    if (!open) {
      initialSnapshotRef.current = null;
      baselineReadyRef.current = false;
      return;
    }
    // Wait for all population effects to settle before locking in the baseline
    if (!baselineReadyRef.current) {
      const timer = setTimeout(() => {
        initialSnapshotRef.current = formSnapshot;
        baselineReadyRef.current = true;
      }, 150);
      return () => clearTimeout(timer);
    }
    // Baseline is set — sync dirty flag with whether values actually differ
    setIsDirty(formSnapshot !== initialSnapshotRef.current);
  }, [open, formSnapshot]);

  // Attempt to close — show warning if unsaved changes exist
  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowLeaveWarning(true);
    } else {
      setValidationErrors([]);
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange]);

  // Confirm leave (discard changes and close)
  const confirmLeave = useCallback(() => {
    setShowLeaveWarning(false);
    setValidationErrors([]);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = async () => {
    if (isSaving) return;

    if (!startDate || !endDate) {
      setValidationErrors(['Start Date', 'End Date']);
      return;
    }

    if (!hasResourceSelection(selectedResourceIds)) {
      setValidationErrors(['Resource (please select at least one)']);
      return;
    }

    setValidationErrors([]);

    // Validate compulsory fields from booking type config
    if (selectedBookingType?.fieldConfigurations) {
      const missingFields: string[] = [];
      for (const fc of selectedBookingType.fieldConfigurations) {
        if (fc.isCompulsory && fc.isVisible) {
          const fn = fc.fieldName.toLowerCase();
          switch (fn) {
            case 'project':
              if (!projectId) missingFields.push(fc.customName || 'Project');
              break;
            case 'cost centre':
              if (!costCentreId) missingFields.push(fc.customName || 'Cost Centre');
              break;
            case 'customer':
              if (!customerId) missingFields.push(fc.customName || 'Customer');
              break;
            case 'booking reference':
              if (!reference) missingFields.push(fc.customName || 'Booking Reference');
              break;
            case 'description':
              if (!description) missingFields.push(fc.customName || 'Description');
              break;
            case 'location':
              if (!address) missingFields.push(fc.customName || 'Location');
              break;
            case 'details':
              if (!details) missingFields.push(fc.customName || 'Details');
              break;
            case 'cost centre task':
              // No dedicated state for cost centre task yet
              break;
            default: {
              // Validate user-defined fields
              const charMatch = fn.match(/^user char (\d+)$/);
              if (charMatch && !userChar[parseInt(charMatch[1])]) {
                missingFields.push(fc.customName || fc.fieldName);
                break;
              }
              const numMatch = fn.match(/^user number (\d+)$/);
              if (numMatch && !userNumber[parseInt(numMatch[1])]) {
                missingFields.push(fc.customName || fc.fieldName);
                break;
              }
              const dateMatch = fn.match(/^user date (\d+)$/);
              if (dateMatch && !userDate[parseInt(dateMatch[1])]) {
                missingFields.push(fc.customName || fc.fieldName);
                break;
              }
              const timeMatch = fn.match(/^user time (\d+)$/);
              if (timeMatch && !userTime[parseInt(timeMatch[1])]) {
                missingFields.push(fc.customName || fc.fieldName);
                break;
              }
              const notesMatch = fn.match(/^user notes (\d+)$/);
              if (notesMatch && !userNotes[parseInt(notesMatch[1])]) {
                missingFields.push(fc.customName || fc.fieldName);
                break;
              }
              break;
            }
          }
        }
      }
      if (missingFields.length > 0) {
        setValidationErrors(missingFields);
        return;
      }
    }

    let finalStartTime: string;
    let finalEndTime: string;
    let halfDayPeriod: HalfDayPeriod | undefined;

    // Format a Date as local ISO string (no "Z" suffix) to prevent timezone offset
    // shifting dates when parsed back with new Date(). This matches the convention
    // used in ResourceCalendar's formatDateAsLocalISO.
    const toLocalISO = (d: Date): string => {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      const ms = String(d.getMilliseconds()).padStart(3, '0');
      return `${y}-${mo}-${da}T${h}:${mi}:${s}.${ms}`;
    };

    if (durationType === 'hourly') {
      const [sH, sM] = startTime.split(':').map(Number);
      const [eH, eM] = endTime.split(':').map(Number);
      const startDT = new Date(startDate);
      startDT.setHours(sH, sM, 0, 0);
      const endDT = new Date(endDate);
      endDT.setHours(eH, eM, 0, 0);
      finalStartTime = toLocalISO(startDT);
      finalEndTime = toLocalISO(endDT);
    } else if (durationType === 'half-day-am') {
      const startDT = new Date(startDate);
      startDT.setHours(0, 0, 0, 0);
      const endDT = new Date(startDate);
      endDT.setHours(11, 59, 0, 0);
      finalStartTime = toLocalISO(startDT);
      finalEndTime = toLocalISO(endDT);
      halfDayPeriod = 'am';
    } else if (durationType === 'half-day-pm') {
      const startDT = new Date(startDate);
      startDT.setHours(12, 0, 0, 0);
      const endDT = new Date(startDate);
      endDT.setHours(23, 59, 0, 0);
      finalStartTime = toLocalISO(startDT);
      finalEndTime = toLocalISO(endDT);
      halfDayPeriod = 'pm';
    } else {
      const startDT = new Date(startDate);
      startDT.setHours(0, 0, 0, 0);
      const endDT = new Date(endDate);
      endDT.setHours(23, 59, 59, 999);
      finalStartTime = toLocalISO(startDT);
      finalEndTime = toLocalISO(endDT);
    }

    const selectedProject = effectiveProjects?.find(p => String(p.id) === projectId);
    const projectName = selectedProject?.name || '';
    const selectedCustomer = clients?.find(c => String(c.id) === String(customerId));

    const effectiveStatus = getBookingStatusForResourceSelection(selectedResourceIds, status as BookingStatus);
    const bookingResourceFields = getBookingResourceFields(selectedResourceIds);

    const bookingData: Omit<Booking, 'id'> = {
      bookingTypeId: bookingTypeId || undefined,
      status: effectiveStatus,
      color: (colour && colour !== 'none') ? colour : '',

      startTime: finalStartTime,
      endTime: finalEndTime,
      durationType: durationType === 'half-day-am' || durationType === 'half-day-pm' ? 'half-day' : durationType,
      halfDayPeriod,
      ...bookingResourceFields,
      projectId: projectId || null,
      projectName,
      costCentreId: costCentreId ? Number(costCentreId) : null,
      clientId: customerId || null,
      billingClassification: 'billable',
      workType: 'remote',
      priority: 'medium',
      bookingReference: reference || undefined,
      customer: selectedCustomer?.name || undefined,
      description: description || undefined,
      location: address || undefined,
      details: details || undefined,
      costCentreTask: costCentreTask || undefined,
      bookingNotes: customerNotes || undefined,
      taskDescription: projectNotes || undefined,
      timezone: bookingTimezone || undefined,
      requiredRole: selectedRole || undefined,
      requiredSkills: requiredSkills,
      userChar: Object.keys(userChar).length > 0 ? userChar : undefined,
      userNumber: Object.keys(userNumber).length > 0 ? userNumber : undefined,
      userFlag: Object.keys(userFlag).length > 0 ? userFlag : undefined,
      userDate: Object.keys(userDate).length > 0 ? userDate : undefined,
      userNotes: Object.keys(userNotes).length > 0 ? userNotes : undefined,
      userTime: Object.keys(userTime).length > 0 ? userTime : undefined,
    };

    if (recurrencePattern !== 'none') {
      bookingData.recurrence = {
        pattern: recurrencePattern,
        interval: parseInt(recurrenceInterval) || 1,
        endType: recurrenceEndType,
        startDate: recurrenceSeriesStartDate ? toLocalISO(recurrenceSeriesStartDate) : undefined,
        endDate: recurrenceEndDate ? toLocalISO(recurrenceEndDate) : undefined,
        occurrences: recurrenceEndType === 'after' ? (parseInt(recurrenceOccurrences) || undefined) : undefined,
        daysOfWeek: recurrencePattern === 'weekly' && recurrenceDaysOfWeek.length > 0 ? recurrenceDaysOfWeek : undefined,
        monthlyRecurrenceType: recurrencePattern === 'monthly' ? recurrenceMonthlyType : undefined,
        dayOfMonth: recurrencePattern === 'monthly' && recurrenceMonthlyType === 'day-of-month' ? recurrenceDayOfMonth : undefined,
        relativeWeekday: recurrencePattern === 'monthly' && recurrenceMonthlyType === 'relative-weekday' ? recurrenceRelativeWeekday : undefined,
      };
    }

    setIsSaving(true);
    try {
      if (isEditing && editingBooking) {
        if (onUpdate) {
          await onUpdate(editingBooking.id, bookingData);
        } else {
          await updateBooking(editingBooking.id, bookingData);
        }
      } else {
        if (onSave) {
          await onSave(bookingData);
        } else {
          await addBooking(bookingData);
        }
      }
      onOpenChange(false);
    } catch (error) {
      // The save was stopped by a confirmation the user declined — keep the form open
      // exactly as it is.
      if (error instanceof BookingSaveCancelledError) return;
      console.error('Failed to save booking:', error);
      setValidationErrors(['An unexpected error occurred. Please try again.']);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (!editingBooking) return;
    if (onDelete) {
      onDelete(editingBooking);
      onOpenChange(false);
    } else if (confirm('Are you sure you want to delete this booking?')) {
      deleteBooking(editingBooking.id)
        .then(() => onOpenChange(false))
        .catch((error: unknown) => {
          console.error('Failed to delete booking:', error);
          alert('Failed to delete booking');
        });
    }
  };



  const hasVisibleProjectFields = useMemo(() => {
    return isFieldVisible('project') || isFieldVisible('costCentre') ||
           isFieldVisible('customer') || isFieldVisible('bookingReference') ||
           isFieldVisible('description') || isFieldVisible('location');
  }, [isFieldVisible]);

  const hasVisibleAddressFields = useMemo(() => {
    return isFieldVisible('location') || isFieldVisible('city') ||
           isFieldVisible('postcode') || isFieldVisible('phone') ||
           isFieldVisible('email');
  }, [isFieldVisible]);

  // Helper: check if a field is assigned to the custom tab
  const isOnCustomTab = useCallback((fieldName: string): boolean => {
    if (!selectedBookingType?.fieldConfigurations) return false;
    const config = selectedBookingType.fieldConfigurations.find(
      (fc: FieldConfiguration) => fc.fieldName?.toLowerCase() === fieldName.toLowerCase()
    );
    return config?.showOnCustomTab === true;
  }, [selectedBookingType]);

  const hasVisibleAdditionalDetails = useMemo(() => {
    // Only count fields that are visible AND not assigned to the custom tab
    const hasField = (name: string) => isFieldVisible(name) && !isOnCustomTab(name);
    return Array.from({ length: 20 }, (_, i) => i + 1).some(i => hasField(`User Char ${i}`)) ||
           Array.from({ length: 10 }, (_, i) => i + 1).some(i => hasField(`User Number ${i}`)) ||
           Array.from({ length: 10 }, (_, i) => i + 1).some(i => hasField(`User Flag ${i}`)) ||
           Array.from({ length: 10 }, (_, i) => i + 1).some(i => hasField(`User Date ${i}`)) ||
           Array.from({ length: 5 }, (_, i) => i + 1).some(i => hasField(`User Time ${i}`)) ||
           Array.from({ length: 5 }, (_, i) => i + 1).some(i => hasField(`User Notes ${i}`));
  }, [isFieldVisible, isOnCustomTab]);

  // Whether any booking types are available for the user to select
  const hasAvailableBookingTypes = useMemo(() => {
    return permittedBookingTypes.some(bt => bt.active && !bt.isHidden);
  }, [permittedBookingTypes]);

  // During tour booking steps, render as a plain div (no Radix Sheet/focus-trap)
  // so the tour overlay buttons remain fully clickable.
  const tourBookingSteps = ['booking-form', 'suggest-resource'];
  const activeTourStepId = isTourActive ? tourSteps[currentStepIndex]?.id : undefined;
  const isTourBookingStep = !!activeTourStepId && tourBookingSteps.includes(activeTourStepId);

  // Listen for tour events to open/close the suggest resource dialog
  useEffect(() => {
    const handleOpen = () => setResourcePickerOpen(true);
    const handleClose = () => setResourcePickerOpen(false);
    window.addEventListener('tour:open-suggest-resource', handleOpen);
    window.addEventListener('tour:close-suggest-resource', handleClose);
    return () => {
      window.removeEventListener('tour:open-suggest-resource', handleOpen);
      window.removeEventListener('tour:close-suggest-resource', handleClose);
    };
  }, []);

  // Shared inner content — rendered identically in both tour and normal modes
  const formContent = (
    <>
          {/* Header */}
          <div className="px-4 pt-3 pb-0 shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => requestClose()}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarDays className="h-4 w-4 text-primary" />
                </div>
                <h2 className="text-lg font-semibold">
                  {isBookingReadOnly ? 'View Booking' : isEditing ? 'Edit Booking' : 'Add Booking'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <FeatureFeedbackCollector feature="Booking Form" />
                {(hasAvailableBookingTypes || isBookingReadOnly) && (() => {
                    const sc = settings?.statusConfigs?.find(s => String(s.id) === status);
                    const bgColor = sc?.color || getDefaultStatusColor(status);
                    const label = sc ? (sc.customText || sc.text) : (status.charAt(0).toUpperCase() + status.slice(1));
                    return (
                      <span
                        className="ml-2 inline-flex items-center rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold"
                        style={{ backgroundColor: bgColor, color: '#000000' }}
                      >
                        {label}
                      </span>
                    );
                  })()}
              </div>
            </div>

            {/* Inline Primary Fields — relative wrapper so button can be absolutely positioned */}
            {(hasAvailableBookingTypes || isBookingReadOnly) && (
            <div className="relative">
              <div className="grid grid-cols-3 gap-1.5 pr-11">
                <div className="space-y-1">
                  <Label htmlFor="booking-type" className="text-xs">
                    {getFieldLabel('Booking Type', 'Booking Type')} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={bookingTypeId} onValueChange={setBookingTypeId} disabled={isBookingReadOnly}>
                    <SelectTrigger id="booking-type" className="h-8 text-xs">
                      <SelectValue placeholder={getFieldPlaceholder('Booking Type', 'Select booking type')} />
                    </SelectTrigger>
                    <SelectContent>
                      {permittedBookingTypes?.filter(type => type.active && !type.isHidden).map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                      {/* When editing/viewing a booking, always include its type so the Select value renders correctly */}
                      {isEditing && bookingTypeId && !permittedBookingTypes?.some(bt => String(bt.id) === bookingTypeId && bt.active && !bt.isHidden) && (() => {
                        const bt = effectiveBookingTypes?.find(t => String(t.id) === bookingTypeId)
                          || bookingTypes?.find(t => String(t.id) === bookingTypeId);
                        return bt ? <SelectItem key={`current-${bt.id}`} value={String(bt.id)}>{bt.name}</SelectItem> : null;
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="status" className="text-xs">
                    {getFieldLabel('Status', 'Status')} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={status} onValueChange={(value) => setStatus(value as BookingStatus)} disabled={isBookingReadOnly}>
                    <SelectTrigger id="status" className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            {option.color && (
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: option.color }} />
                            )}
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isFieldVisible('Colour') && (
                  <div className="space-y-1">
                    <Label htmlFor="color-tag" className="text-xs">{getFieldLabel('Colour', 'Colour')}</Label>
                    <Select value={colour} onValueChange={setColour} disabled={isBookingReadOnly}>
                      <SelectTrigger id="color-tag" className="h-8 text-xs">
                        <SelectValue placeholder={getFieldPlaceholder('Colour', 'Select colour')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {colourOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: option.color }} />
                              {option.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Expand/collapse — absolutely pinned to right-0 bottom-0, aligns with accordion chevrons */}
              {hasAvailableBookingTypes && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={toggleAllAccordions}
                      className={cn(
                        'absolute bottom-0 right-1 h-8 w-8 rounded border flex items-center justify-center transition-all duration-200 outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        allExpanded
                          ? 'bg-primary/8 border-primary/30 text-primary hover:bg-primary/15'
                          : 'bg-muted/40 border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <ChevronsUpDown
                        className={cn(
                          'h-4 w-4 transition-transform duration-300 shrink-0',
                          allExpanded ? 'rotate-180' : 'rotate-0'
                        )}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              )}
            </div>
            )}
          </div>



          {/* Read-only banner */}
          {isBookingReadOnly && (
            <div className="mx-4 mt-2 mb-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <span className="text-xs font-medium text-amber-800">This booking type is read-only. You can view but not edit.</span>
            </div>
          )}

          {/* Scrollable Content */}
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport className="h-full" ref={skillsScrollRef}>
              {!hasAvailableBookingTypes && !isBookingReadOnly && bookingTypeUsages !== null ? (
                <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                    <CalendarDays className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">Cannot Add Booking</h3>
                  <p className="text-xs text-muted-foreground max-w-[300px]">
                    You do not have full permission to any booking type or you have only view permission. Please contact your administrator to configure booking type permissions.
                  </p>
                </div>
              ) : (
              <div className={cn("px-4 pt-3 pb-2", isBookingReadOnly && "pointer-events-none opacity-60")}>

                {/* ── Resource Conflict Banner ─────────────────────────────── */}
                {filteredConflicts.length > 0 && (() => {
                  // Group conflicts by booking so each booking appears once
                  const grouped = filteredConflicts.reduce<Record<string, { name: string; resources: string[]; overlapStart: Date; overlapEnd: Date }>>((acc, c) => {
                    const key = String(c.conflictingBookingId);
                    if (!acc[key]) {
                      acc[key] = { name: c.conflictingProjectName, resources: [], overlapStart: c.overlapStart, overlapEnd: c.overlapEnd };
                    }
                    if (!acc[key].resources.includes(c.resourceName)) {
                      acc[key].resources.push(c.resourceName);
                    }
                    return acc;
                  }, {});
                  const groups = Object.values(grouped);
                  const affectedResources = [...new Set(filteredConflicts.map(c => c.resourceName))];

                  const SHOW_LIMIT = 2;
                  const visibleGroups = showAllConflicts ? groups : groups.slice(0, SHOW_LIMIT);
                  const hiddenCount = groups.length - SHOW_LIMIT;

                  return (
                    <div className="mb-3 mt-2 rounded-lg border border-amber-200 bg-amber-50 shadow-sm overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center justify-between px-3 pt-2 pb-1.5">
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          <span className="text-xs font-semibold text-amber-900">
                            {groups.length === 1 ? '1 Scheduling Conflict' : `${groups.length} Scheduling Conflicts`}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setResourcePickerOpen(true)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-amber-800 bg-amber-200/70 hover:bg-amber-300/80 transition-colors duration-150 shrink-0"
                        >
                          <Sparkles className="h-3 w-3" />
                          Find resource
                        </button>
                      </div>

                      {/* Conflict rows */}
                      <div className="border-t border-amber-200/60 divide-y divide-amber-100">
                        {visibleGroups.map((group, idx) => {
                          const s = group.overlapStart;
                          const e = group.overlapEnd;
                          const isSameDay = s.toDateString() === e.toDateString();
                          const isFullDay = s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 23 && e.getMinutes() === 59;
                          const dateStr = isSameDay ? format(s, 'dd MMM') : `${format(s, 'dd MMM')}–${format(e, 'dd MMM')}`;
                          const timeStr = !isFullDay ? ` · ${format(s, 'HH:mm')}–${format(e, 'HH:mm')}` : '';
                          return (
                            <div key={idx} className="flex items-center gap-2 px-3 py-1.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                              <span className="text-xs font-medium text-amber-900 truncate">{group.name}</span>
                              <span className="text-[11px] text-amber-600 shrink-0">{dateStr}{timeStr}</span>
                              <span className="text-[11px] text-amber-500 truncate ml-auto">{group.resources.join(', ')}</span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Show more / footer */}
                      <div className="flex items-center justify-between px-3 py-1.5 border-t border-amber-200/60">
                        {hiddenCount > 0 && !showAllConflicts ? (
                          <button
                            type="button"
                            onClick={() => setShowAllConflicts(true)}
                            className="text-[11px] text-amber-700 hover:text-amber-900 font-medium transition-colors"
                          >
                            +{hiddenCount} more conflict{hiddenCount > 1 ? 's' : ''} — show all
                          </button>
                        ) : showAllConflicts && groups.length > SHOW_LIMIT ? (
                          <button
                            type="button"
                            onClick={() => setShowAllConflicts(false)}
                            className="text-[11px] text-amber-700 hover:text-amber-900 font-medium transition-colors"
                          >
                            Show less
                          </button>
                        ) : (
                          <span />
                        )}
                        <p className="text-[11px] text-amber-600">Double-check before saving.</p>
                      </div>
                    </div>
                  );
                })()}



                <Accordion type="multiple" value={accordionValue} onValueChange={setAccordionValue} className="space-y-1">

                  {/* Render main sections in configured order */}
                  {orderedSections.map((sectionId) => {
                    if (sectionId === 'datetime') {
                      return (
                        <AccordionItem key="schedule" value="schedule" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">Schedule</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-2">
                            <div className="space-y-3">

                              {/* ── Duration + Repeat two-column grid ── */}
                              <div className="grid gap-x-3" style={{ gridTemplateColumns: '1fr auto' }}>

                                {/* Left column — Duration cards */}
                                <div className="space-y-1.5">
                                  <Label className="text-xs text-muted-foreground font-medium">Duration <span className="text-destructive">*</span></Label>

                                  {/* Four duration cards */}
                                  <div className="grid grid-cols-4 gap-3">
                                    {(
                                      [
                                        { value: 'full-day',    bold: 'Full Day',   sub: '00:00 – 23:59' },
                                        { value: 'half-day-am', bold: 'Morning',    sub: '00:00 \u2013 11:59' },
                                        { value: 'half-day-pm', bold: 'Afternoon',  sub: '12:00 \u2013 23:59' },
                                        { value: 'hourly',      bold: 'Hourly',     sub: 'Custom'        },
                                      ] as const
                                    ).map(({ value, bold, sub }) => {
                                      const active = durationType === value;
                                      return (
                                        <button
                                          key={value}
                                          type="button"
                                          onClick={() => handleDurationTypeChange(value)}
                                          className={cn(
                                            'flex flex-col items-center justify-center rounded-md border px-1.5 py-2 text-center transition-colors cursor-pointer',
                                            active
                                              ? 'border-primary bg-primary/5 text-primary'
                                              : 'border-border bg-background text-foreground hover:border-primary/50 hover:bg-muted/40'
                                          )}
                                        >
                                          <span className={cn('text-[11px] font-semibold leading-tight', active ? 'text-primary' : 'text-foreground')}>{bold}</span>
                                          <span className={cn('text-[10px] leading-tight mt-0.5', active ? 'text-primary/70' : 'text-muted-foreground')}>{sub}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Right column — Repeat card */}
                                {selectedBookingType?.allowRepeatBookings !== false && (
                                  <div className="flex flex-col space-y-1.5 min-w-[72px]">
                                    <Label className="text-xs text-muted-foreground font-medium">Repeat</Label>
                                    <button
                                      type="button"
                                      onClick={() => setRecurrenceDialogOpen(true)}
                                      className={cn(
                                        'flex flex-1 flex-col items-center justify-center rounded-md border px-4 w-full text-center transition-colors cursor-pointer gap-0.5',
                                        recurrencePattern !== 'none'
                                          ? 'border-primary bg-primary/5 text-primary'
                                          : 'border-dashed border-border bg-background text-muted-foreground hover:border-primary/50 hover:bg-muted/40'
                                      )}
                                    >
                                      <Repeat className={cn('h-3.5 w-3.5', recurrencePattern !== 'none' ? 'text-primary' : 'text-primary/70')} />
                                      <span className={cn('text-xs font-semibold leading-tight', recurrencePattern !== 'none' ? 'text-primary' : 'text-foreground')}>
                                        {recurrencePattern !== 'none' ? 'Edit' : 'Set'}
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Recurrence summary pill — visible only when a pattern is set */}
                              {recurrencePattern !== 'none' && (
                                <button
                                  type="button"
                                  onClick={() => setRecurrenceDialogOpen(true)}
                                  className="flex items-center gap-1.5 w-full bg-primary/5 border border-primary/20 rounded-full px-3 py-1 text-left hover:bg-primary/10 transition-colors"
                                >
                                  <Repeat className="h-3 w-3 text-primary shrink-0" />
                                  <span className="text-xs text-primary flex-1">
                                    {getRecurrenceSummaryText()}
                                  </span>
                                  <X
                                    className="h-3 w-3 text-primary/60 hover:text-primary shrink-0"
                                    onClick={(e) => { e.stopPropagation(); handleRecurrenceRemove(); }}
                                  />
                                </button>
                              )}

                              {/* ── Date / Time fields ── */}
                              <div className="space-y-2 min-h-[72px]">

                                {/* HOURLY — single row: date + start time → end time */}
                                {durationType === 'hourly' && (() => {
                                  const isOvernight = !!startTime && !!endTime && endTime < startTime;
                                  const overnightEndDate = startDate ? addDays(startDate, 1) : null;
                                  return (
                                    <div className="flex items-start gap-3">
                                      {/* Date — sets both start and end date; end date auto-advances if overnight */}
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground font-medium">Date <span className="text-destructive">*</span></Label>
                                        <Popover open={startDateOpen} onOpenChange={(open) => { if (!open) setStartDateOpen(false); }}>
                                          <PopoverTrigger asChild>
                                            <Button variant="outline" className={cn('w-[145px] h-8 text-xs justify-start text-left font-normal px-2', !startDate && 'text-muted-foreground')} onClick={() => setStartDateOpen(true)}>
                                              <CalendarDays className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                                              {startDate ? format(startDate, 'dd MMM yyyy') : 'Select date'}
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent mode="single" selected={startDate} onSelect={(date) => { const selected = date ?? startDate; setStartDate(selected); setEndDate(isOvernight && selected ? addDays(selected, 1) : selected); setStartDateOpen(false); }} initialFocus />
                                          </PopoverContent>
                                        </Popover>
                                      </div>
                                      {/* From time */}
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground font-medium">From <span className="text-destructive">*</span></Label>
                                        <Input type="time" value={startTime} onChange={(e) => { const val = e.target.value; setStartTime(val); const overnight = !!endTime && endTime < val; setEndDate(overnight && startDate ? addDays(startDate, 1) : startDate); }} className="h-8 text-xs w-[105px]" />
                                      </div>
                                      {/* Arrow — aligned to centre of input row */}
                                      <div className="flex flex-col justify-end" style={{ height: '52px' }}>
                                        <div className="h-8 flex items-center">
                                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                                        </div>
                                      </div>
                                      {/* To time + overnight indicator */}
                                      <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground font-medium">To <span className="text-destructive">*</span></Label>
                                        <Input type="time" value={endTime} onChange={(e) => { const val = e.target.value; setEndTime(val); const overnight = !!startTime && val < startTime; setEndDate(overnight && startDate ? addDays(startDate, 1) : startDate); }} className="h-8 text-xs w-[105px]" />
                                        {isOvernight && overnightEndDate && (
                                          <p className="text-[10px] text-amber-600 font-medium flex items-center gap-0.5 pt-0.5">
                                            <Moon className="h-2.5 w-2.5" />
                                            Ends {format(overnightEndDate, 'dd MMM')}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}

                                {/* FULL DAY — Start date → End date in one row */}
                                {durationType === 'full-day' && (
                                  <div className="flex items-end gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs text-muted-foreground font-medium">Start date <span className="text-destructive">*</span></Label>
                                      <Popover open={startDateOpen} onOpenChange={(open) => { if (!open) setStartDateOpen(false); }}>
                                        <PopoverTrigger asChild>
                                          <Button variant="outline" className={cn('w-[145px] h-8 text-xs justify-start text-left font-normal px-2', !startDate && 'text-muted-foreground')} onClick={() => setStartDateOpen(true)}>
                                            <CalendarDays className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                                            {startDate ? format(startDate, 'dd MMM yyyy') : 'Select date'}
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                          <CalendarComponent mode="single" selected={startDate} onSelect={(date) => { const selected = date ?? startDate; setStartDate(selected); if (selected && (!endDate || selected > endDate)) setEndDate(selected); setStartDateOpen(false); const isEndDateDefault = !endDate || (selected && endDate.toDateString() === selected.toDateString()); if (isEndDateDefault) setTimeout(() => setEndDateOpen(true), 100); }} initialFocus />
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 mb-2 shrink-0" />
                                    <div className="space-y-1">
                                      <Label className="text-xs text-muted-foreground font-medium">End date <span className="text-destructive">*</span></Label>
                                      <Popover open={endDateOpen} onOpenChange={(open) => { if (!open) setEndDateOpen(false); }}>
                                        <PopoverTrigger asChild>
                                          <Button variant="outline" className={cn('w-[145px] h-8 text-xs justify-start text-left font-normal px-2', !endDate && 'text-muted-foreground')} onClick={() => setEndDateOpen(true)}>
                                            <CalendarDays className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                                            {endDate ? format(endDate, 'dd MMM yyyy') : 'Select date'}
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                          <CalendarComponent mode="single" selected={endDate} onSelect={(date) => { const selected = date ?? endDate; setEndDate(selected); if (selected && startDate && selected < startDate) setStartDate(selected); setEndDateOpen(false); }} initialFocus />
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                )}

                                {/* AM / PM — Start date only */}
                                {(durationType === 'half-day-am' || durationType === 'half-day-pm') && (
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground font-medium">Start date <span className="text-destructive">*</span></Label>
                                    <Popover open={startDateOpen} onOpenChange={(open) => { if (!open) setStartDateOpen(false); }}>
                                      <PopoverTrigger asChild>
                                        <Button variant="outline" className={cn('w-[145px] h-8 text-xs justify-start text-left font-normal px-2', !startDate && 'text-muted-foreground')} onClick={() => setStartDateOpen(true)}>
                                          <CalendarDays className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                                          {startDate ? format(startDate, 'dd MMM yyyy') : 'Select date'}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarComponent mode="single" selected={startDate} onSelect={(date) => { const selected = date ?? startDate; setStartDate(selected); if (selected && (!endDate || selected > endDate)) setEndDate(selected); setStartDateOpen(false); }} initialFocus />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                )}

                              </div>

                              {settings?.enableTimezones && (
                                <div className="space-y-1">
                                  <Label className="text-xs">
                                    <div className="flex items-center gap-1.5">
                                      <Globe className="h-3 w-3" />
                                      Timezone
                                    </div>
                                  </Label>
                                  <Select value={bookingTimezone} onValueChange={setBookingTimezone}>
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Select timezone" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(() => {
                                        const filtered = TIMEZONES.filter(
                                          tz => !settings?.availableTimezones?.length || settings.availableTimezones.includes(tz.value)
                                        );
                                        const currentTzInList = bookingTimezone && filtered.some(tz => tz.value === bookingTimezone);
                                        if (bookingTimezone && !currentTzInList) {
                                          const match = TIMEZONES.find(tz => tz.value === bookingTimezone);
                                          if (match) filtered.unshift(match);
                                        }
                                        return filtered.map((tz) => (
                                          <SelectItem key={tz.value} value={tz.value}>
                                            {tz.label}
                                          </SelectItem>
                                        ));
                                      })()}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    if (sectionId === 'resources') {
                      const selectedResObjs = pickerResources.filter(r =>
                        selectedResourceIds.includes(String(r.id))
                      );

                      return (
                        <AccordionItem key="resources" value="resources" className="border rounded-lg overflow-hidden">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline hover:bg-muted/30 transition-colors">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Users className="h-4 w-4 text-primary shrink-0" />
                              <span className="text-sm font-medium">Resources</span>

                              {selectedResObjs.length === 0 && (
                                <span className="text-xs text-muted-foreground/70 ml-1">None assigned</span>
                              )}
                            </div>
                          </AccordionTrigger>

                          <AccordionContent className="px-3 pb-2 pt-0">
                            <div className="space-y-3">
                              {/* ── Resource picker row ────────────────────────────────────────────── */}
                              {/* Single-resource layout: picker + Role in two equal columns */}
                              {/* Multi-resource layout: picker full-width; Role moves into per-resource rows below */}
                              {!isMultiResourceMode ? (
                                /* ── SINGLE-RESOURCE LAYOUT (default, ≤1 resource) ── */
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Resource */}
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <Label className="text-xs text-muted-foreground font-medium">Resource</Label>
                                      <button
                                        type="button"
                                        data-tour="suggest-resource-btn"
                                        onClick={() => setResourcePickerOpen(true)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-all duration-150"
                                      >
                                        <Sparkles className="h-3 w-3" />
                                        Suggest Resource
                                      </button>
                                    </div>
                                    <EnhancedMultiResourceSelect
                                      resources={pickerResources}
                                      selectedResourceIds={selectedResourceIds}
                                      onSelect={handleResourceSelect}
                                      placeholder="Select resources..."
                                      className="h-8 text-xs"
                                      calculateUtilization={calculateUtilization}
                                      utilisationTiers={settings?.utilisationTiers}
                                    />
                                  </div>
                                  {/* Role — booking-level, single-resource mode only */}
                                  {/* TODO: back end to update binding to per-resource when multi-resource is supported */}
                                  <div className="flex flex-col justify-end space-y-1">
                                    <Label className="text-xs text-muted-foreground font-medium">Role</Label>
                                    <Popover open={roleOpen} onOpenChange={setRoleOpen}>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          role="combobox"
                                          aria-expanded={roleOpen}
                                          className="w-full h-8 justify-between text-xs font-normal text-muted-foreground"
                                        >
                                          {selectedRole || 'Select Role'}
                                          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="w-[--radix-popover-trigger-width] p-0"
                                        align="start"
                                        side="bottom"
                                        sideOffset={4}
                                      >
                                        <Command>
                                          <CommandInput
                                            placeholder="Search roles…"
                                            className="h-8 text-xs"
                                          />
                                          <CommandList>
                                            <CommandEmpty className="py-3 text-xs text-muted-foreground text-center">
                                              No roles found.
                                            </CommandEmpty>
                                            <CommandGroup>
                                              {(rolesFromSelectedResources.length > 0
                                                ? rolesFromSelectedResources
                                                : allResourceRoles
                                              ).map((role) => {
                                                const coverageCount = resources.filter((r) => r.role === role).length;
                                                const isSelected = selectedRole === role;
                                                return (
                                                  <CommandItem
                                                    key={role}
                                                    value={role}
                                                    onSelect={() => {
                                                      setSelectedRole(isSelected ? '' : role);
                                                      setRoleOpen(false);
                                                    }}
                                                    className="flex items-center justify-between gap-2 text-xs py-1.5 cursor-pointer"
                                                  >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                      <Check
                                                        className={cn(
                                                          'h-3.5 w-3.5 shrink-0 text-primary transition-opacity',
                                                          isSelected ? 'opacity-100' : 'opacity-0'
                                                        )}
                                                      />
                                                      <span className="truncate">{role}</span>
                                                    </div>
                                                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                                                      {coverageCount} {coverageCount === 1 ? 'resource' : 'resources'}
                                                    </span>
                                                  </CommandItem>
                                                );
                                              })}
                                            </CommandGroup>
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                </div>
                              ) : (
                                /* ── MULTI-RESOURCE LAYOUT (2+ resources) ── */
                                /* Resource picker spans full width; Role + Skills move into per-resource rows below */
                                <div className="space-y-1.5">
                                  {/* Unified container — picker + resource rows share one border */}
                                  <div className="rounded-lg border border-border overflow-hidden divide-y divide-border">
                                    {/* Resource picker row */}
                                    <div className="flex items-center gap-2 px-2 py-1.5 bg-card">
                                      <div className="flex-[2_2_0%] min-w-0">
                                        <EnhancedMultiResourceSelect
                                          resources={pickerResources}
                                          selectedResourceIds={selectedResourceIds}
                                          onSelect={handleResourceSelect}
                                          placeholder="Select resources..."
                                          className="h-8 text-xs w-full"
                                          calculateUtilization={calculateUtilization}
                                          utilisationTiers={settings?.utilisationTiers}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        data-tour="suggest-resource-btn"
                                        onClick={() => setResourcePickerOpen(true)}
                                        className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 px-2 py-1 h-8 text-[11px] font-medium rounded-md border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-all duration-150 whitespace-nowrap"
                                      >
                                        <Sparkles className="h-3 w-3 shrink-0" />
                                        Suggest Resource
                                      </button>
                                    </div>

                                  {/* Per-resource assignment rows */}
                                    {selectedResourceIds.map((resourceId) => {
                                      const resource = resources.find((r) => String(r.id) === resourceId);
                                      if (!resource) return null;

                                      const assignment = perResourceAssignments.get(resourceId) ?? {
                                        role: '',
                                        skills: [],
                                        skillsOpen: false,
                                      };

                                      const displayName = resource.name.trim();

                                      // Avatar — deterministic colour from name
                                      const avatarPalette = [
                                        'bg-blue-500','bg-violet-500','bg-emerald-500',
                                        'bg-amber-500','bg-rose-500','bg-cyan-500',
                                        'bg-indigo-500','bg-pink-500',
                                      ];
                                      const avatarColour = avatarPalette[
                                        displayName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % avatarPalette.length
                                      ];
                                      const nameParts = displayName.split(' ').filter(Boolean);
                                      const initials = nameParts.length >= 2
                                        ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
                                        : displayName.slice(0, 2).toUpperCase();

                                      // Roles for this resource
                                      const resourceRoleOptions = resource.role
                                        ? [resource.role]
                                        : allResourceRoles;

                                      const rowSkillNames = allSkillNames;

                                      // Skills — cap visible chips at 3, show +N overflow
                                      const SKILL_CAP = 3;
                                      const visibleSkills = assignment.skills.slice(0, SKILL_CAP);
                                      const overflowCount = assignment.skills.length - SKILL_CAP;

                                      return (
                                        <div key={resourceId} className="bg-card">
                                          {/* ── Single horizontal row: Name | Role | Skills ── */}
                                          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,2fr)] items-center gap-2 px-3 py-2">

                                            {/* Col 1 — Avatar + full name */}
                                            <div className="flex items-center gap-1.5 min-w-0">
                                              {resource.photoUrl ? (
                                                <img
                                                  src={resource.photoUrl}
                                                  alt={displayName}
                                                  className="h-6 w-6 rounded-md object-cover shrink-0 block"
                                                />
                                              ) : (
                                                <div className={cn(
                                                  'h-6 w-6 rounded-md flex items-center justify-center shrink-0 text-white',
                                                  avatarColour
                                                )}>
                                                  <span className="text-[10px] font-semibold leading-none">{initials}</span>
                                                </div>
                                              )}
                                              <span className="text-xs font-medium text-foreground truncate">
                                                {displayName}
                                              </span>
                                            </div>

                                            {/* Col 2 — Role dropdown (centre) */}
                                            {/* TODO: wire to backend — per-resource role value is not yet submitted */}
                                            <div className="min-w-0">
                                              <Popover
                                                open={assignment.roleOpen}
                                                onOpenChange={(open) =>
                                                  updatePerResourceAssignment(resourceId, { roleOpen: open })
                                                }
                                              >
                                                <PopoverTrigger asChild>
                                                  <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    aria-expanded={assignment.roleOpen}
                                                    className="w-full h-7 justify-between text-xs font-normal text-muted-foreground"
                                                  >
                                                    <span className="truncate">{assignment.role || 'Select Role'}</span>
                                                    <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                                  </Button>
                                                </PopoverTrigger>
                                                <PopoverContent
                                                  className="w-[--radix-popover-trigger-width] p-0"
                                                  align="start"
                                                  side="bottom"
                                                  sideOffset={4}
                                                >
                                                  <Command>
                                                    <CommandInput
                                                      placeholder="Search roles…"
                                                      className="h-8 text-xs"
                                                    />
                                                    <CommandList>
                                                      <CommandEmpty className="py-3 text-xs text-muted-foreground text-center">
                                                        No roles found.
                                                      </CommandEmpty>
                                                      <CommandGroup>
                                                        {resourceRoleOptions.map((role) => {
                                                          const isSelected = assignment.role === role;
                                                          return (
                                                            <CommandItem
                                                              key={role}
                                                              value={role}
                                                              onSelect={() => {
                                                                handlePerResourceRoleChange(resourceId, isSelected ? '' : role);
                                                                updatePerResourceAssignment(resourceId, { roleOpen: false });
                                                              }}
                                                              className="flex items-center gap-2 text-xs py-1.5 cursor-pointer"
                                                            >
                                                              <Check
                                                                className={cn(
                                                                  'h-3.5 w-3.5 shrink-0 text-primary transition-opacity',
                                                                  isSelected ? 'opacity-100' : 'opacity-0'
                                                                )}
                                                              />
                                                              <span className="truncate">{role}</span>
                                                            </CommandItem>
                                                          );
                                                        })}
                                                      </CommandGroup>
                                                    </CommandList>
                                                  </Command>
                                                </PopoverContent>
                                              </Popover>
                                            </div>

                                            {/* Col 3 — Preferred Skills picker (right) */}
                                            {/* TODO: wire to backend — per-resource skills are not yet submitted */}
                                            <div className="min-w-0 flex items-center flex-nowrap gap-1 overflow-hidden">
                                                {visibleSkills.map((skill) => (
                                                  <span
                                                    key={skill}
                                                    className="inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 shrink-0"
                                                  >
                                                    <span className="max-w-[72px] truncate">{skill}</span>
                                                    <button
                                                      type="button"
                                                      onClick={() => togglePerResourceSkill(resourceId, skill)}
                                                      className="rounded-full p-0.5 hover:bg-primary/20 hover:text-destructive transition-colors"
                                                      aria-label={`Remove ${skill}`}
                                                    >
                                                      <X className="h-2 w-2" />
                                                    </button>
                                                  </span>
                                                ))}
                                                {overflowCount > 0 && (
                                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-muted text-muted-foreground border border-border shrink-0">
                                                    +{overflowCount}
                                                  </span>
                                                )}

                                                {/* Skills popover trigger */}
                                                <Popover
                                                  open={assignment.skillsOpen}
                                                  onOpenChange={(open) =>
                                                    updatePerResourceAssignment(resourceId, { skillsOpen: open })
                                                  }
                                                >
                                                  <TooltipProvider delayDuration={400}>
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <PopoverTrigger asChild>
                                                          <button
                                                            type="button"
                                                            className={cn(
                                                              'inline-flex items-center gap-1 rounded-full border text-[11px] font-medium transition-all duration-150',
                                                              assignment.skills.length === 0
                                                                ? 'px-2 py-0.5 h-7 w-full justify-center border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5'
                                                                : 'px-1.5 py-0.5 border-dashed border-border/60 text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5'
                                                            )}
                                                            aria-label="Add skills"
                                                          >
                                                            <span className="text-sm leading-none">+</span>
                                                            {assignment.skills.length === 0 && (
                                                              <span>Add skills</span>
                                                            )}
                                                          </button>
                                                        </PopoverTrigger>
                                                      </TooltipTrigger>
                                                      <TooltipContent side="top" className="text-xs">
                                                        Add skill
                                                      </TooltipContent>
                                                    </Tooltip>
                                                  </TooltipProvider>
                                                  <PopoverContent
                                                    className="w-64 p-0"
                                                    align="end"
                                                  side="bottom"
                                                  sideOffset={4}
                                                  collisionPadding={8}
                                                >
                                                  <Command>
                                                    <CommandInput
                                                      placeholder="Search skills…"
                                                      className="h-9 text-sm"
                                                    />
                                                    <CommandList>
                                                      <CommandEmpty className="py-3 text-xs text-muted-foreground text-center">
                                                        No skills found.
                                                      </CommandEmpty>
                                                      <CommandGroup>
                                                        {rowSkillNames.map((name) => {
                                                          const maxRating = resources.reduce<number>(
                                                            (best, r) => {
                                                              const s = (r.skills ?? []).find(
                                                                (sk) => sk.name.toLowerCase() === name.toLowerCase()
                                                              );
                                                              return s ? Math.max(best, s.rating) : best;
                                                            },
                                                            0
                                                          );
                                                          const coverageCount = resources.filter((r) =>
                                                            (r.skills ?? []).some(
                                                              (s) => s.name.toLowerCase() === name.toLowerCase()
                                                            )
                                                          ).length;
                                                          const isSelected = assignment.skills.includes(name);
                                                          return (
                                                            <CommandItem
                                                              key={name}
                                                              value={name}
                                                              onSelect={() => togglePerResourceSkill(resourceId, name)}
                                                              className="flex items-center justify-between gap-2 text-sm py-2 cursor-pointer"
                                                            >
                                                              <div className="flex items-center gap-2 min-w-0">
                                                                <Check
                                                                  className={cn(
                                                                    'h-3.5 w-3.5 shrink-0 text-primary transition-opacity',
                                                                    isSelected ? 'opacity-100' : 'opacity-0'
                                                                  )}
                                                                />
                                                                <span className="truncate text-sm">{name}</span>
                                                              </div>
                                                              <div className="flex items-center gap-2 shrink-0">
                                                                <span className="flex items-center gap-0.5">
                                                                  {[1, 2, 3, 4, 5].map((i) => (
                                                                    <span
                                                                      key={i}
                                                                      className={cn(
                                                                        'inline-block w-1.5 h-1.5 rounded-full',
                                                                        i <= maxRating ? 'bg-primary' : 'bg-muted-foreground/20'
                                                                      )}
                                                                    />
                                                                  ))}
                                                                </span>
                                                                <span className="text-[10px] text-muted-foreground tabular-nums w-12 text-right">
                                                                  {coverageCount}{' '}
                                                                  {coverageCount === 1 ? 'resource' : 'resources'}
                                                                </span>
                                                              </div>
                                                            </CommandItem>
                                                          );
                                                        })}
                                                      </CommandGroup>
                                                    </CommandList>
                                                  </Command>
                                                </PopoverContent>
                                              </Popover>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Required Skills — booking-level, single-resource mode only */}
                              {/* TODO: back end to update binding to per-resource */}
                              {!isMultiResourceMode && (
                                <div className="mt-2 space-y-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <Label className="text-xs text-muted-foreground font-medium">Required Skills</Label>
                                    {requiredSkills.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        ({requiredSkills.length} selected)
                                      </span>
                                    )}
                                  </div>

                                  {/* Selected skill tags */}
                                  {requiredSkills.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-border bg-muted/30">
                                      {requiredSkills.map((skill) => {
                                        const coverageCount = resources.filter((r) =>
                                          (r.skills ?? []).some((s) => s.name.toLowerCase() === skill.toLowerCase())
                                        ).length;
                                        return (
                                          <span
                                            key={skill}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20"
                                          >
                                            {skill}
                                            {coverageCount > 0 && (
                                              <span className="text-[9px] text-muted-foreground font-normal">
                                                {coverageCount}
                                              </span>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => removeSkill(skill)}
                                              className="ml-0.5 rounded-full hover:text-destructive transition-colors"
                                              aria-label={`Remove ${skill}`}
                                            >
                                              <X className="h-2.5 w-2.5" />
                                            </button>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Popover lookup list — Popover + Command pattern consistent with shadcn comboboxes */}
                                  <Popover open={skillsOpen} onOpenChange={setSkillsOpen}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={skillsOpen}
                                        className="w-full h-8 justify-between text-xs font-normal text-muted-foreground"
                                      >
                                        {requiredSkills.length > 0
                                          ? `${requiredSkills.length} skill${requiredSkills.length !== 1 ? 's' : ''} selected`
                                          : 'Select required skills…'}
                                        <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-[--radix-popover-trigger-width] p-0"
                                      align="start"
                                      side="bottom"
                                      sideOffset={4}
                                      collisionBoundary={skillsScrollRef.current}
                                      collisionPadding={8}
                                    >
                                      <Command>
                                        <CommandInput
                                          placeholder="Search skills…"
                                          className="h-8 text-xs"
                                        />
                                        <CommandList>
                                          <CommandEmpty className="py-3 text-xs text-muted-foreground text-center">
                                            No skills found.
                                          </CommandEmpty>
                                          <CommandGroup>
                                            {allSkillNames.map((name) => {
                                              // Find the highest rating across all resources for this skill
                                              const maxRating = resources.reduce<number>((best, r) => {
                                                const s = (r.skills ?? []).find(
                                                  (sk) => sk.name.toLowerCase() === name.toLowerCase()
                                                );
                                                return s ? Math.max(best, s.rating) : best;
                                              }, 0);
                                              const coverageCount = resources.filter((r) =>
                                                (r.skills ?? []).some((s) => s.name.toLowerCase() === name.toLowerCase())
                                              ).length;
                                              const isSelected = requiredSkills.includes(name);
                                              return (
                                                <CommandItem
                                                  key={name}
                                                  value={name}
                                                  onSelect={() => toggleSkill(name)}
                                                  className="flex items-center justify-between gap-2 text-xs py-1.5 cursor-pointer"
                                                >
                                                  <div className="flex items-center gap-2 min-w-0">
                                                    <Check
                                                      className={cn(
                                                        'h-3.5 w-3.5 shrink-0 text-primary transition-opacity',
                                                        isSelected ? 'opacity-100' : 'opacity-0'
                                                      )}
                                                    />
                                                    <span className="truncate">{name}</span>
                                                  </div>
                                                  <div className="flex items-center gap-2 shrink-0">
                                                    {/* Proficiency dots — max rating across all resources */}
                                                    <span className="flex items-center gap-0.5">
                                                      {[1, 2, 3, 4, 5].map((i) => (
                                                        <span
                                                          key={i}
                                                          className={cn(
                                                            'inline-block w-1.5 h-1.5 rounded-full',
                                                            i <= maxRating ? 'bg-primary' : 'bg-muted-foreground/20'
                                                          )}
                                                        />
                                                      ))}
                                                    </span>
                                                    {/* Resource coverage count */}
                                                    <span className="text-[10px] text-muted-foreground tabular-nums w-12 text-right">
                                                      {coverageCount} {coverageCount === 1 ? 'resource' : 'resources'}
                                                    </span>
                                                  </div>
                                                </CommandItem>
                                              );
                                            })}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    if (sectionId === 'booking-details') {
                      return (
                        <React.Fragment key="booking-details">
                          {/* Project Details Section - fields rendered in configured order */}
                          {hasVisibleProjectFields && (
                            <AccordionItem value="project-details" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                              <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                                <div className="flex items-center gap-2">
                                  <Briefcase className="h-4 w-4 text-primary" />
                                  <span className="text-sm font-medium">Booking Details</span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-3 pb-2">
                                <div className="grid grid-cols-2 gap-2">
                                  {/* Render fields in configured order, 2 per row */}
                                  {orderedProjectFields.filter(fieldName => {
                                    const fn = fieldName.toLowerCase();
                                    if (fn === 'project') return isFieldVisible('Project');
                                    if (fn === 'cost centre') return isFieldVisible('Cost Centre');
                                    if (fn === 'cost centre task') return isFieldVisible('Cost Centre Task');
                                    if (fn === 'customer') return isFieldVisible('Customer');
                                    if (fn === 'booking reference') return isFieldVisible('Booking Reference');
                                    if (fn === 'description') return isFieldVisible('Description');
                                    if (fn === 'location') return isFieldVisible('Location');
                                    if (fn === 'details') return isFieldVisible('Details');
                                    return false;
                                  }).map(fieldName => {
                                    const fn = fieldName.toLowerCase();
                                    if (fn === 'project') {
                                      return (
                                        <div key="project" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Project', 'Project')} {isFieldCompulsory('Project') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <EnhancedProjectSelect
                                            projects={filteredProjects as unknown as ProjectDetails[]}
                                            selectedProjectId={projectId}
                                            onSelect={handleProjectChange}
                                            placeholder={getFieldPlaceholder('Project', 'Select project...')}
                                          />
                                        </div>
                                      );
                                    }
                                    if (fn === 'cost centre') {
                                      return (
                                        <div key="cost-centre" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Cost Centre', 'Cost Centre')} {isFieldCompulsory('Cost Centre') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <EnhancedCostCentreSelect
                                            costCentres={filteredCostCentres}
                                            allCostCentres={costCentres || []}
                                            selectedCostCentreId={costCentreId}
                                            onSelect={(id: string) => { setCostCentreId(id); setCostCentreTask(''); }}
                                            disabled={!projectId}
                                            placeholder={!projectId ? 'Select a project first...' : getFieldPlaceholder('Cost Centre', 'Select cost centre...')}
                                            projectId={projectId}
                                          />
                                        </div>
                                      );
                                    }
                                    if (fn === 'cost centre task') {
                                      return (
                                        <div key="cost-centre-task" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Cost Centre Task', 'Cost Centre Task')} {isFieldCompulsory('Cost Centre Task') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <Input
                                            value={costCentreTask}
                                            onChange={(e) => setCostCentreTask(e.target.value)}
                                            disabled={!costCentreId}
                                            placeholder={!costCentreId ? 'Select a cost centre first...' : getFieldPlaceholder('Cost Centre Task', 'Enter cost centre task')}
                                            className="h-8 text-xs"
                                          />
                                        </div>
                                      );
                                    }
                                    if (fn === 'customer') {
                                      return (
                                        <div key="customer" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Customer', 'Customer')} {isFieldCompulsory('Customer') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <EnhancedCustomerSelect
                                            customers={clients || []}
                                            selectedCustomerId={customerId}
                                            onSelect={handleCustomerChange}
                                            placeholder={getFieldPlaceholder('Customer', 'Select customer...')}
                                          />
                                        </div>
                                      );
                                    }
                                    if (fn === 'booking reference') {
                                      return (
                                        <div key="booking-reference" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Booking Reference', 'Booking Reference')} {isFieldCompulsory('Booking Reference') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder={getFieldPlaceholder('Booking Reference', 'Enter booking reference')} className="h-8 text-xs" />
                                        </div>
                                      );
                                    }
                                    if (fn === 'description') {
                                      return (
                                        <div key="description" className="col-span-2 space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Description', 'Description')} {isFieldCompulsory('Description') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={getFieldPlaceholder('Description', 'Enter description')} className="min-h-[60px] resize-none text-xs" />
                                        </div>
                                      );
                                    }
                                    if (fn === 'location') {
                                      return (
                                        <div key="location" className="space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Location', 'Location')} {isFieldCompulsory('Location') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={getFieldPlaceholder('Location', 'Enter location')} className="h-8 text-xs" />
                                        </div>
                                      );
                                    }
                                    if (fn === 'details') {
                                      return (
                                        <div key="details" className="col-span-2 space-y-1">
                                          <Label className="text-xs">
                                            {getFieldLabel('Details', 'Details')} {isFieldCompulsory('Details') && <span className="text-destructive">*</span>}
                                          </Label>
                                          <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={getFieldPlaceholder('Details', 'Enter details')} className="min-h-[60px] resize-none text-xs" />
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}

                          {/* Additional Details Section - User-defined fields rendered in configured order */}
                          {hasVisibleAdditionalDetails && (
                            <AccordionItem value="additional-details" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                              <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                                <div className="flex items-center gap-2">
                                  <SlidersHorizontal className="h-4 w-4 text-primary" />
                                  <span className="text-sm font-medium">Additional Details</span>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="px-3 pb-2">
                                <div className="space-y-2.5">
                                  {/* Render user-defined fields in fieldOrder if available */}
                                  {(() => {
                                    // Get visible user-defined fields, excluding those assigned to the custom tab
                                    const visibleUserFields: { fieldName: string; type: string; index: number }[] = [];
                                    for (let i = 1; i <= 20; i++) {
                                      if (isFieldVisible(`User Char ${i}`) && !isOnCustomTab(`User Char ${i}`)) visibleUserFields.push({ fieldName: `User Char ${i}`, type: 'char', index: i });
                                    }
                                    for (let i = 1; i <= 10; i++) {
                                      if (isFieldVisible(`User Number ${i}`) && !isOnCustomTab(`User Number ${i}`)) visibleUserFields.push({ fieldName: `User Number ${i}`, type: 'number', index: i });
                                    }
                                    for (let i = 1; i <= 10; i++) {
                                      if (isFieldVisible(`User Flag ${i}`) && !isOnCustomTab(`User Flag ${i}`)) visibleUserFields.push({ fieldName: `User Flag ${i}`, type: 'flag', index: i });
                                    }
                                    for (let i = 1; i <= 10; i++) {
                                      if (isFieldVisible(`User Date ${i}`) && !isOnCustomTab(`User Date ${i}`)) visibleUserFields.push({ fieldName: `User Date ${i}`, type: 'date', index: i });
                                    }
                                    for (let i = 1; i <= 5; i++) {
                                      if (isFieldVisible(`User Time ${i}`) && !isOnCustomTab(`User Time ${i}`)) visibleUserFields.push({ fieldName: `User Time ${i}`, type: 'time', index: i });
                                    }
                                    for (let i = 1; i <= 5; i++) {
                                      if (isFieldVisible(`User Notes ${i}`) && !isOnCustomTab(`User Notes ${i}`)) visibleUserFields.push({ fieldName: `User Notes ${i}`, type: 'notes', index: i });
                                    }

                                    // Sort by fieldOrder if available
                                    if (selectedBookingType?.fieldOrder && selectedBookingType.fieldOrder.length > 0) {
                                      const orderMap = new Map(selectedBookingType.fieldOrder.map((f: string, idx: number) => [f.toLowerCase(), idx]));
                                      visibleUserFields.sort((a, b) => {
                                        const aIdx = orderMap.get(a.fieldName.toLowerCase()) ?? 9999;
                                        const bIdx = orderMap.get(b.fieldName.toLowerCase()) ?? 9999;
                                        return aIdx - bIdx;
                                      });
                                    }

                                    return (
                                      <div className="grid grid-cols-2 gap-2">
                                        {visibleUserFields.map(field => {
                                          const fc = selectedBookingType?.fieldConfigurations?.find(
                                            (f: FieldConfiguration) => f.fieldName?.toLowerCase() === field.fieldName.toLowerCase()
                                          );
                                          const label = fc?.customName || field.fieldName;
                                          const required = fc?.isCompulsory || false;

                                          if (field.type === 'char') {
                                            return (
                                              <div key={field.fieldName} className="space-y-1">
                                                <Label className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                                <Input
                                                  value={userChar[field.index] || ''}
                                                  onChange={(e) => setUserChar(prev => ({ ...prev, [field.index]: e.target.value }))}
                                                  placeholder={`Enter ${label.toLowerCase()}`}
                                                  className="h-8 text-xs"
                                                />
                                              </div>
                                            );
                                          }
                                          if (field.type === 'number') {
                                            return (
                                              <div key={field.fieldName} className="space-y-1">
                                                <Label className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                                <Input
                                                  type="number"
                                                  value={userNumber[field.index] || ''}
                                                  onChange={(e) => setUserNumber(prev => ({ ...prev, [field.index]: e.target.value }))}
                                                  placeholder={`Enter ${label.toLowerCase()}`}
                                                  className="h-8 text-xs"
                                                />
                                              </div>
                                            );
                                          }
                                          if (field.type === 'flag') {
                                            return (
                                              <div key={field.fieldName} className="flex items-center gap-2">
                                                <Checkbox
                                                  id={`user-flag-${field.index}`}
                                                  checked={userFlag[field.index] || false}
                                                  onCheckedChange={(checked) => setUserFlag(prev => ({ ...prev, [field.index]: checked === true }))}
                                                />
                                                <Label htmlFor={`user-flag-${field.index}`} className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                              </div>
                                            );
                                          }
                                          if (field.type === 'date') {
                                            return (
                                              <div key={field.fieldName} className="space-y-1">
                                                <Label className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                                <Input
                                                  type="date"
                                                  value={userDate[field.index] || ''}
                                                  onChange={(e) => setUserDate(prev => ({ ...prev, [field.index]: e.target.value }))}
                                                  className="h-8 text-xs"
                                                />
                                              </div>
                                            );
                                          }
                                          if (field.type === 'time') {
                                            return (
                                              <div key={field.fieldName} className="space-y-1">
                                                <Label className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                                <Input
                                                  type="time"
                                                  value={userTime[field.index] || ''}
                                                  onChange={(e) => setUserTime(prev => ({ ...prev, [field.index]: e.target.value }))}
                                                  className="h-8 text-xs"
                                                />
                                              </div>
                                            );
                                          }
                                          if (field.type === 'notes') {
                                            return (
                                              <div key={field.fieldName} className="col-span-2 space-y-1">
                                                <Label className="text-xs">
                                                  {label} {required && <span className="text-destructive">*</span>}
                                                </Label>
                                                <Textarea
                                                  value={userNotes[field.index] || ''}
                                                  onChange={(e) => setUserNotes(prev => ({ ...prev, [field.index]: e.target.value }))}
                                                  placeholder={`Enter ${label.toLowerCase()}`}
                                                  className="min-h-[60px] resize-none text-xs"
                                                />
                                              </div>
                                            );
                                          }
                                          return null;
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}

                  {/* Tab sections rendered in configured order with visibility and custom labels */}
                  {orderedTabs.map((tabName) => {
                    if (tabName === 'address' && isTabVisible('address') && hasVisibleAddressFields) {
                      return (
                        <AccordionItem key="address-contact" value="address-contact" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">{getTabLabel('address', 'Address & Contact')}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-3">
                            <div className="space-y-3">

                              {/* Address fields */}
                              <div className="space-y-2">

                                {/* Address Line 1 */}
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">
                                    Address Line 1

                                  </Label>
                                  <Textarea
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder="Address line 1"
                                    className="text-xs resize-none min-h-[56px] leading-relaxed"
                                    rows={2}
                                  />
                                </div>

                                {/* Address Line 2 */}
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">
                                    Address Line 2
                                    <span className="text-muted-foreground/60 font-normal ml-1">(optional)</span>
                                  </Label>
                                  <Input
                                    value={addressLine2}
                                    onChange={(e) => setAddressLine2(e.target.value)}
                                    placeholder="Apartment, suite, unit, building, floor, etc."
                                    className="h-8 text-xs"
                                  />
                                </div>

                                {/* Town/City */}
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Town / City</Label>
                                  <Input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Town or city"
                                    className="h-8 text-xs"
                                  />
                                </div>

                                {/* County + Postcode side by side */}
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">County</Label>
                                    <Input
                                      value={county}
                                      onChange={(e) => setCounty(e.target.value)}
                                      placeholder="County"
                                      className="h-8 text-xs"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Postcode</Label>
                                    <Input
                                      value={postcode}
                                      onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                                      placeholder="e.g. SW1A 1AA"
                                      maxLength={8}
                                      className="h-8 text-xs font-mono tracking-wide"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Divider */}
                              <div className="border-t border-border/60" />

                              {/* Contact fields */}
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Phone</Label>
                                    <Input
                                      type="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="e.g. 020 7946 0958"
                                      className="h-8 text-xs"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Email</Label>
                                    <Input
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="email@example.com"
                                      className="h-8 text-xs"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Source note */}
                              {customerId && (
                                <p className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                                  Populated from customer record. Edit here to override for this booking only.
                                </p>
                              )}

                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    if (tabName === 'history' && isTabVisible('history')) {
                      // Use API data as the source of truth. Only fall back to
                      // in-memory entries when the API hasn't returned data yet
                      // (avoids duplicates caused by mismatched ids between
                      // client-generated and DB-generated audit records).
                      const inMemory = editingBooking?.auditHistory ?? [];
                      const merged = apiAuditHistory.length > 0
                        ? apiAuditHistory
                        : inMemory;
                      const auditEntries: BookingAuditEntry[] = merged.map(entry => ({
                        ...entry,
                        changes: entry.changes.filter(c => c.field !== 'Project Name'),
                      }));
                      const sortedEntries = [...auditEntries].sort(
                        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                      );

                      const formatAuditDateTime = (iso: string): string => {
                        const d = new Date(iso);
                        const dd = String(d.getDate()).padStart(2, '0');
                        const mon = d.toLocaleString('en-GB', { month: 'short' });
                        const yyyy = d.getFullYear();
                        const hh = String(d.getHours()).padStart(2, '0');
                        const min = String(d.getMinutes()).padStart(2, '0');
                        const ss = String(d.getSeconds()).padStart(2, '0');
                        return `${dd} ${mon}, ${yyyy} ${hh}:${min}:${ss}`;
                      };

                      return (
                        <AccordionItem key="booking-history" value="booking-history" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <History className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">{getTabLabel('history', 'Booking History')}</span>
                              {sortedEntries.length > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                                  {sortedEntries.length}
                                </span>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-3">
                            {sortedEntries.length === 0 ? (
                              <div className="py-6 text-center text-xs text-muted-foreground">
                                No history yet
                              </div>
                            ) : (
                              <div className="relative mt-1 space-y-0">
                                {/* Vertical timeline line */}
                                <div className="absolute left-[19px] top-4 bottom-4 w-px bg-border" />

                                {sortedEntries.map((entry, idx) => {
                                  const isCreated = entry.action === 'created';
                                  return (
                                    <div key={entry.id} className="relative flex gap-3 py-2.5">
                                      {/* Timeline dot + avatar */}
                                      <div className="relative z-10 shrink-0">
                                        <Avatar className="h-9 w-9 ring-2 ring-background">
                                          {entry.performedByAvatar && (
                                            <AvatarImage src={entry.performedByAvatar} />
                                          )}
                                          <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                            {entry.performedByInitials}
                                          </AvatarFallback>
                                        </Avatar>
                                      </div>

                                      {/* Content bubble */}
                                      <div className="flex-1 min-w-0 rounded-lg border bg-muted/30 px-3 py-2">
                                        {/* Top row: name + action badge + timestamp */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs font-semibold text-foreground">{entry.performedBy}</span>
                                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                            isCreated
                                              ? 'bg-primary/10 text-primary'
                                              : 'bg-amber-500/10 text-amber-600'
                                          }`}>
                                            {isCreated ? 'Created booking' : 'Updated booking'}
                                          </span>
                                          <span className="ml-auto text-[10px] text-muted-foreground whitespace-nowrap">
                                            {formatAuditDateTime(entry.timestamp)}
                                          </span>
                                        </div>

                                        {/* Field changes */}
                                        {entry.changes.length > 0 && (
                                          <div className="mt-1.5 space-y-1">
                                            {entry.changes.map((change, ci) => (
                                              <div key={ci} className="text-[11px] text-muted-foreground">
                                                <span className="font-medium text-foreground">{change.field}</span>
                                                {change.oldValue !== null && change.newValue !== null && change.newValue !== '' ? (
                                                  <span> — <span className="opacity-70">Old Value: </span><span className="text-foreground">{change.oldValue}</span> → <span className="opacity-70">New Value: </span><span className="text-foreground">{change.newValue}</span></span>
                                                ) : change.oldValue !== null ? (
                                                  <span> — <span className="opacity-70">Old Value: </span><span className="text-foreground">{change.oldValue}</span></span>
                                                ) : (
                                                  <span> — <span className="opacity-70">New Value: </span><span className="text-foreground">{change.newValue ?? '—'}</span></span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    if (tabName === 'project-notes' && isTabVisible('project-notes')) {
                      return (
                        <AccordionItem key="project-notes" value="project-notes" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">{getTabLabel('project-notes', 'Project Notes')}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-2">
                            <Textarea
                              value={projectNotes}
                              onChange={(e) => setProjectNotes(e.target.value)}
                              placeholder="Add internal project notes..."
                              className="min-h-[80px] resize-none text-xs"
                            />
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    if (tabName === 'custom' && isTabVisible('custom')) {
                      // Custom tab shows fields marked with showOnCustomTab
                      const customTabFields = selectedBookingType?.fieldConfigurations?.filter(
                        (fc: FieldConfiguration) => fc.showOnCustomTab && fc.isVisible
                      ) || [];
                      if (customTabFields.length === 0) return null;
                      return (
                        <AccordionItem key="custom-tab" value="custom-tab" className="border rounded-lg focus-within:ring-0 focus-within:ring-offset-0 focus-within:border-border">
                          <AccordionTrigger className="px-3 py-1.5 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <SlidersHorizontal className="h-4 w-4 text-primary" />
                              <span className="text-sm font-medium">{getTabLabel('custom', 'Custom')}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-2">
                            <div className="grid grid-cols-2 gap-2">
                              {customTabFields.map((fc: FieldConfiguration) => {
                                const label = fc.customName || fc.fieldName;
                                const required = fc.isCompulsory || false;
                                const fn = fc.fieldName.toLowerCase();
                                const charMatch = fn.match(/^user char (\d+)$/);
                                const numMatch = fn.match(/^user number (\d+)$/);
                                const flagMatch = fn.match(/^user flag (\d+)$/);
                                const dateMatch = fn.match(/^user date (\d+)$/);
                                const timeMatch = fn.match(/^user time (\d+)$/);
                                const notesMatch = fn.match(/^user notes (\d+)$/);

                                if (charMatch) {
                                  const idx = parseInt(charMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="space-y-1">
                                      <Label className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                      <Input value={userChar[idx] || ''} onChange={(e) => setUserChar(prev => ({ ...prev, [idx]: e.target.value }))} placeholder={`Enter ${label.toLowerCase()}`} className="h-8 text-xs" />
                                    </div>
                                  );
                                }
                                if (numMatch) {
                                  const idx = parseInt(numMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="space-y-1">
                                      <Label className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                      <Input type="number" value={userNumber[idx] || ''} onChange={(e) => setUserNumber(prev => ({ ...prev, [idx]: e.target.value }))} placeholder={`Enter ${label.toLowerCase()}`} className="h-8 text-xs" />
                                    </div>
                                  );
                                }
                                if (flagMatch) {
                                  const idx = parseInt(flagMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="flex items-center gap-2">
                                      <Checkbox id={`custom-flag-${idx}`} checked={userFlag[idx] || false} onCheckedChange={(checked) => setUserFlag(prev => ({ ...prev, [idx]: checked === true }))} />
                                      <Label htmlFor={`custom-flag-${idx}`} className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                    </div>
                                  );
                                }
                                if (dateMatch) {
                                  const idx = parseInt(dateMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="space-y-1">
                                      <Label className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                      <Input type="date" value={userDate[idx] || ''} onChange={(e) => setUserDate(prev => ({ ...prev, [idx]: e.target.value }))} className="h-8 text-xs" />
                                    </div>
                                  );
                                }
                                if (timeMatch) {
                                  const idx = parseInt(timeMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="space-y-1">
                                      <Label className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                      <Input type="time" value={userTime[idx] || ''} onChange={(e) => setUserTime(prev => ({ ...prev, [idx]: e.target.value }))} className="h-8 text-xs" />
                                    </div>
                                  );
                                }
                                if (notesMatch) {
                                  const idx = parseInt(notesMatch[1]);
                                  return (
                                    <div key={fc.fieldName} className="col-span-2 space-y-1">
                                      <Label className="text-xs">{label} {required && <span className="text-destructive">*</span>}</Label>
                                      <Textarea value={userNotes[idx] || ''} onChange={(e) => setUserNotes(prev => ({ ...prev, [idx]: e.target.value }))} placeholder={`Enter ${label.toLowerCase()}`} className="min-h-[60px] resize-none text-xs" />
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    }

                    return null;
                  })}

                </Accordion>
              </div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div className="px-4 pt-3 shrink-0">
              <Alert variant="default" size="default" className="border-[#E53935] bg-[#FDECEA] [&>svg]:text-[#D32F2F]">
                <AlertTitle className="text-[#D32F2F] font-medium">Please complete the required fields before saving</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 list-disc list-inside space-y-0.5 text-[#333333]">
                    {validationErrors.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </AlertDescription>
                <AlertClose onClick={() => setValidationErrors([])} className="text-[#D32F2F] hover:text-[#B71C1C]" />
              </Alert>
            </div>
          )}

          {/* Footer */}
          <SheetFooter className="px-4 py-2.5 border-t shrink-0 flex-row justify-between">
            {isEditing && (!isBookingReadOnly || deleteOnlyMode) && (
              <Button type="button" variant="destructive" size="sm" onClick={handleDelete} className="h-8">
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={() => requestClose()} className="h-8">
                <X className="h-4 w-4 mr-1.5" />
                {isBookingReadOnly ? 'Close' : 'Cancel'}
              </Button>
              {!isBookingReadOnly && (
                <Button type="button" size="sm" onClick={handleSave} className="h-8" disabled={isSaving || (!hasAvailableBookingTypes && bookingTypeUsages !== null)}>
                  <Save className="h-4 w-4 mr-1.5" />
                  {isSaving ? 'Saving...' : isEditing ? 'Update Booking' : 'Save'}
                </Button>
              )}
            </div>
          </SheetFooter>
    </>
  );

  return (
    <>
      {/* Tour mode: plain fixed div — no Radix Sheet, no focus-trap, tour buttons stay clickable */}
      {isTourBookingStep ? (
        open && (
          <>
            {/* Dim overlay — purely visual, no pointer capture */}
            <div className="fixed inset-0 bg-black/50 z-50" style={{ pointerEvents: 'none' }} aria-hidden="true" />
            {/* Panel */}
            <div
              data-tour="booking-form"
              className="bg-background fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col shadow-lg sm:max-w-[700px] animate-in slide-in-from-right duration-500"
            >
              {formContent}
            </div>
          </>
        )
      ) : (
        <SheetPrimitive.Root open={open} onOpenChange={(val) => { if (!val) requestClose(); }}>
          <SheetPrimitive.Portal>
            <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <SheetPrimitive.Content
              data-tour="booking-form"
              className="bg-background fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:duration-300 data-[state=open]:duration-500 sm:max-w-[700px]"
            >
              {formContent}
            </SheetPrimitive.Content>
          </SheetPrimitive.Portal>
        </SheetPrimitive.Root>
      )}

      {/* Recurrence Dialog */}
      {/* Unsaved-changes leave warning */}
      <AlertDialog open={showLeaveWarning} onOpenChange={setShowLeaveWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              If you leave now your changes will be lost. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowLeaveWarning(false)}>
              Stay &amp; keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmLeave}
            >
              Leave &amp; discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecurrenceDialog
        open={recurrenceDialogOpen}
        onOpenChange={setRecurrenceDialogOpen}
        recurrencePattern={recurrencePattern}
        onRecurrencePatternChange={setRecurrencePattern}
        customInterval={recurrenceInterval}
        onCustomIntervalChange={setRecurrenceInterval}
        recurrenceEndType={recurrenceEndType}
        onRecurrenceEndTypeChange={setRecurrenceEndType}
        occurrences={recurrenceOccurrences}
        onOccurrencesChange={setRecurrenceOccurrences}
        recurrenceEndDate={recurrenceEndDate}
        onRecurrenceEndDateChange={setRecurrenceEndDate}
        onDaysOfWeekChange={setRecurrenceDaysOfWeek}
        onDayOfMonthChange={setRecurrenceDayOfMonth}
        onMonthlyRecurrenceTypeChange={setRecurrenceMonthlyType}
        onRelativeWeekdayChange={setRecurrenceRelativeWeekday}
        onSeriesStartDateChange={setRecurrenceSeriesStartDate}
        daysOfWeek={recurrenceDaysOfWeek}
        monthlyRecurrenceType={recurrenceMonthlyType}
        startDate={startDate}
        bookingEndDate={endDate}
        startTime={startTime}
        endTime={endTime}
        durationType={durationType}
      />

      <ResourceRecommendationDialog
        open={resourcePickerOpen}
        onOpenChange={setResourcePickerOpen}
        isTourMode={isTourActive}
        startDate={startDate}
        endDate={endDate}
        requiredRole={selectedRole || undefined}
        location={address || undefined}
        requiredSkills={requiredSkills.length > 0 ? requiredSkills : stableEmptySkills}
        projectId={projectId || null}
        projects={stableProjects}
        resources={recommendationResources}
        existingBookings={stableExistingBookings}
        currentResourceIds={selectedResourceIds}
        onSelectionChange={handleResourceSelect}
        onConfirm={(ids) => {
          handleResourceSelect(ids);
          setResourcePickerOpen(false);
        }}
      />
    </>
  );
}
